﻿/* global React, Icon, Button, Field, Input, Select, Currency, Segmented, StatusBadge, KPI, Modal, EmptyState, FilterPanel, makeFilter, dateInFilter, toast, DataList, PdfViewer, fmt, InvoiceRecipientField, invoiceSaveRecipientContact, invoiceSendRecipientOptions */
// PLUMME 3 - Fakturaliste, backed by app-state through PlummeBackend.

// KPI labels and help use stable IDs instead of translated display text.
const fakKpiLabel = (id) => t('inv.kpi.' + id);
const fakKpiHelp = (id) => t('inv.kpi.' + id + '.help');

const INVOICE_LIST_TABS = ['faktura', 'tilbud', 'ordre', 'kreditnota', 'utkast'];
const INVOICE_LIST_TABS_WITH_REPORT = [...INVOICE_LIST_TABS, 'salgsrapport', 'importhistorikk'];
// Firestore keeps Norwegian document types; translation-key references stay English.
const DRAFT_TYPES = ['faktura', 'tilbud', 'ordre', 'kreditnota'];
const draftTypeOptions = () => DRAFT_TYPES.map((type) => ({ key: '__draft_' + type, type, label: t(PlummeI18n.keyFor('inv.type', type, 'documentType')) }));
const invoiceListTypeLabel = (type) => t(PlummeI18n.keyFor('inv.type', type, 'documentType'), t('inv.type.unknown'));
const sendActionLabel = (inv) => (inv.type === 'kreditnota'
  ? t('inv.act.sendCreditNote')
  : inv.sentAt || inv.status === 'sendt' ? t('inv.act.sendAgain') : t('inv.act.send'));

function invoiceListYear(invoice) {
  const text = String(invoice?.date || '');
  const nbMatch = text.match(/(?:^|\D)(\d{4})(?:\D|$)/);
  if (nbMatch) return Number(nbMatch[1]);
  const isoMatch = text.match(/^(\d{4})-\d{2}-\d{2}/);
  if (isoMatch) return Number(isoMatch[1]);
  return new Date().getFullYear();
}

function consumeInvoiceListRouteIntent() {
  const intent = window.__plummeInvoiceListFilter || null;
  const requestedTab = window.__plummeInvoiceListTab || intent?.tab;
  window.__plummeInvoiceListFilter = null;
  window.__plummeInvoiceListTab = null;
  if (!intent && !requestedTab) return null;
  return {
    ...(intent || {}),
    tab: INVOICE_LIST_TABS_WITH_REPORT.includes(requestedTab) ? requestedTab : intent?.tab,
  };
}

function consumeInvoiceListBilagReturnFlag() {
  const shouldReturn = Boolean(window.__bilagReturn);
  window.__bilagReturn = false;
  return shouldReturn;
}

// Prefix distinguishes this return button from other classic-script globals.
function InvoiceListBackToBilagButton({ show, setRoute }) {
  if (!show) return null;
  return <Button size="sm" variant="secondary" icon="arrowLeft" onClick={() => setRoute ? setRoute('allebilag') : window.plummeNav && window.plummeNav('allebilag')}>{t('inv.backToVouchers')}</Button>;
}

function makeInvoiceListFilter(intent) {
  const base = makeFilter();
  return { ...base, ...(intent?.filter || {}), checks: { ...(base.checks || {}), ...(intent?.checks || intent?.filter?.checks || {}) } };
}

// To varianter med vilje. E-postemnet gaar til kunden og skal foelge dokumentets
// spraak, ikke brukerens UI-spraak — en norsk kunde skal ikke faa engelsk emnefelt
// bare fordi regnskapsfoereren har byttet spraak i appen.
function documentLabelForRecipient(inv) {
  if (inv.type === 'kreditnota') return 'kreditnota';
  if (inv.type === 'tilbud') return 'tilbud';
  if (inv.type === 'ordre') return 'ordrebekreftelse';
  return 'faktura';
}

function documentLabelCap(inv) {
  return t(PlummeI18n.keyFor('inv.type', inv.type, 'documentType'), t('inv.type.invoice'));
}

function invoiceListCustomerEmail(inv) {
  return inv?._backend?.customer?.email || inv?.customerEmail || '';
}

function invoiceListSendSubject(inv) {
  const label = documentLabelForRecipient(inv);
  return `${label.charAt(0).toUpperCase() + label.slice(1)} ${inv?.num || ''} fra Plumme`.trim();
}

function invoiceListSendPayload(inv, email) {
  const backendInvoice = inv._backend || inv;
  return {
    ...backendInvoice,
    customer: {
      ...(backendInvoice.customer || {}),
      name: backendInvoice.customer?.name || inv.customer || '',
      email,
    },
  };
}

// Massehandlingene ligger i `invoice-list-bulk.jsx`: hvem hver handling kan treffe
// (`invoiceListCanBulkSelect` og resten), dialogen som bekrefter den, og kjoringen.

function invoiceListAddDays(iso, days) {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  date.setDate(date.getDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
}

function invoiceListToIsoDate(value, fallback = invoiceListTodayIso()) {
  const text = String(value || '').trim();
  const nb = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (nb) return `${nb[3]}-${nb[2].padStart(2, '0')}-${nb[1].padStart(2, '0')}`;
  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  return fallback;
}

function invoiceListOutstandingAmount(inv) {
  const remaining = Number(inv?.remainingAmount);
  if (Number.isFinite(remaining) && remaining > 0) return remaining;
  return Math.max(0, Number(inv?.amount) || 0);
}

function invoiceListOpenDraftPayload(invoice) {
  if (!invoice) return null;
  const kind = invoice.kind || invoice._backend?.kind;
  const customer = invoice.customer?.name || invoice.customer || t('inv.fallback.noCustomer');
  const type = kind === 'offer' ? 'tilbud' : kind === 'order_confirmation' ? 'ordre' : kind === 'credit' ? 'kreditnota' : invoice.type || 'faktura';
  return {
    ...invoice,
    id: invoice.id,
    num: invoice.invoiceNumber || invoice.num || t('inv.draftPlaceholder'),
    type,
    status: 'utkast',
    draft: true,
    customer,
    date: invoice.date || invoice.createdAt || '',
    due: invoice.dueDate || invoice.validUntil || '',
    _backend: invoice._backend || invoice,
  };
}

function amountInFilter(amount, filter) {
  if (!filter?.amtOn) return true;
  const value = Math.abs(Number(amount) || 0);
  const parse = (input) => {
    if (input === '' || input == null) return null;
    return window.PlummeMoneyInput.parse(input, { preferDecimal: true });
  };
  const from = parse(filter.amtFrom);
  const to = parse(filter.amtTo);
  if (from != null && value < from) return false;
  if (to != null && value > to) return false;
  return true;
}

function invoiceListLineLabel(line, index) {
  return line?.productName || line?.description || line?.name || t('inv.fallback.line', { n: index + 1 });
}

function invoiceListLineNet(line) {
  const qty = Number(line?.quantity ?? line?.qty ?? 1) || 1;
  const unit = Number(line?.unitPrice ?? line?.price ?? 0) || 0;
  const base = qty * unit;
  const discount = Number(line?.discountValue ?? line?.disc ?? 0) || 0;
  const amount = line?.discountType === 'amount' || line?.discMode === 'kr' ? base - discount : base - (base * discount / 100);
  return Math.max(0, amount);
}

function invoiceListLineVatRate(line) {
  const rate = Number(line?.vatRate ?? line?.vat ?? 25);
  return Number.isFinite(rate) ? rate : 25;
}

// Linjene slik krediteringsdialogen trenger dem: hva som staar paa fakturaen, hva som
// allerede er kreditert, og hva som dermed er igjen. Backend regner ut resten
// (`creditableLines`); fallbacken dekker rader uten lagrede linjer, som importert
// historikk, og trekker da fra det som er kreditert paa dokumentet som helhet.
function invoiceListCreditableLines(inv) {
  if (Array.isArray(inv.creditableLines) && inv.creditableLines.length) return inv.creditableLines;
  const backendLines = Array.isArray(inv._backend?.lines) ? inv._backend.lines : [];
  const credited = Math.max(0, Number(inv.creditedAmount) || 0);
  if (backendLines.length) {
    return backendLines.map((line, index) => {
      const net = Math.abs(invoiceListLineNet(line));
      const vatRate = line.vatTreatment && line.vatTreatment !== 'standard' ? 0 : invoiceListLineVatRate(line);
      const quantity = Math.abs(Number(line.quantity ?? 1) || 1);
      return {
        sourceLineId: String(line.id || `line-${index + 1}`),
        description: invoiceListLineLabel(line, index),
        quantity, unitPrice: Math.abs(Number(line.unitPrice ?? 0) || 0), vatRate,
        net, gross: net * (1 + vatRate / 100),
        creditedQuantity: 0, creditedNet: 0, creditedGross: 0,
        remainingQuantity: quantity, remainingNet: net, remainingGross: net * (1 + vatRate / 100),
      };
    });
  }
  const net = Math.abs(inv.net || 0);
  const gross = Math.abs(inv.amount || 0);
  const vatRate = net ? Math.round((gross / net - 1) * 100) : 25;
  const remainingGross = Math.max(0, gross - credited);
  return [{
    sourceLineId: 'line-1',
    description: t('inv.credit.fallbackDescription', { document: inv.num || t('inv.type.invoice').toLowerCase() }),
    quantity: 1, unitPrice: net, vatRate,
    net, gross,
    creditedQuantity: 0, creditedNet: 0, creditedGross: credited,
    remainingQuantity: 1,
    remainingNet: vatRate ? remainingGross / (1 + vatRate / 100) : remainingGross,
    remainingGross,
  }];
}

function CreditDialog({ inv, onClose, onCreate, busy, initialSelections = null }) {
  const allLines = invoiceListCreditableLines(inv);
  // Ferdig krediterte linjer vises ikke igjen. De kan ikke velges, og en «Full
  // kreditering» skal bare ta det som faktisk staar igjen.
  const sourceLines = allLines.filter((line) => line.remainingGross > 0.005);
  const closedLines = allLines.length - sourceLines.length;
  const partiallyCredited = allLines.some((line) => line.creditedGross > 0.005); const initialFor=(line)=>Array.isArray(initialSelections)?initialSelections.find((entry)=>String(entry.sourceLineId)===String(line.sourceLineId)):null;
  const [mode, setMode] = useState(Array.isArray(initialSelections) ? 'partial' : 'full');
  const [selected, setSelected] = useState(() => sourceLines.map((line) => Array.isArray(initialSelections) ? Boolean(initialFor(line)) : true));
  const [quantities, setQuantities] = useState(() => sourceLines.map((line) => String(initialFor(line)?.quantity??line.remainingQuantity)));
  const [amounts, setAmounts] = useState(() => sourceLines.map((line) => String(Math.round(initialFor(line)?.netAmount!=null?Number(initialFor(line).netAmount)*(1+Number(line.vatRate||0)/100):line.remainingGross))));
  const minimumCreditDate = invoiceListCreditMinimumDate(inv); const [creditDate, setCreditDate] = useState(() => [invoiceListTodayIso(), minimumCreditDate].sort().at(-1)); const [confirmPaid, setConfirmPaid] = useState(false);
  const parseAmount = (value) => window.PlummeMoneyInput.number(value, 0);
  const lineCredit = (line, index) => {
    const maxQty = line.remainingQuantity;
    const maxGross = Math.round(line.remainingGross);
    const qty = mode === 'full' ? maxQty : Math.min(maxQty, Math.max(0, Number(String(quantities[index] || '').replace(',', '.')) || 0));
    const qtyShare = maxQty ? Math.min(1, qty / maxQty) : 0;
    const gross = mode === 'full' ? maxGross : Math.min(Math.round(maxGross * qtyShare), Math.max(0, parseAmount(amounts[index])));
    const vatRate = line.vatRate;
    const net = vatRate ? gross / (1 + (vatRate / 100)) : gross;
    return { qty, net, gross, maxGross };
  };
  const credits = sourceLines.map(lineCredit);
  const gross = credits.reduce((sum, row, index) => sum + ((mode === 'full' || selected[index]) ? row.gross : 0), 0);
  const net = Math.round(credits.reduce((sum, row, index) => sum + ((mode === 'full' || selected[index]) ? row.net : 0), 0));
  const selections = mode === 'full'
    ? sourceLines.map((line) => ({ sourceLineId: line.sourceLineId, quantity: line.remainingQuantity, netAmount: line.remainingNet }))
    : sourceLines.map((line, index) => selected[index] ? { sourceLineId: line.sourceLineId, quantity: credits[index].qty, netAmount: credits[index].net } : null).filter(Boolean);
  const formatKr = (value) => fmt(Math.round(value || 0));
  const invalidDate = !creditDate || creditDate < minimumCreditDate; const createCredit = () => onCreate({ selections, partial: mode === 'partial', net, gross, date: creditDate });
  const submitCredit = () => Number(inv.paidAmount) > 0.005 ? setConfirmPaid(true) : createCredit();
  // Portalen holder dialogen utenfor tabellraden som flyttes ved backendoppdateringer.
  if (confirmPaid) return <Modal open portal onClose={() => setConfirmPaid(false)} title={t('inv.credit.paidConfirmTitle')} max={500}
    footer={<React.Fragment><Button variant="ghost" onClick={() => setConfirmPaid(false)}>{t('common.cancel')}</Button><Button variant="danger-solid" icon="files" disabled={busy} onClick={createCredit}>{t('inv.credit.paidConfirmContinue')}</Button></React.Fragment>}>
    <p className="t-body" style={{ margin: 0 }}>{t('inv.credit.paidConfirmBody', { amount: formatKr(inv.paidAmount) })}</p>
  </Modal>;
  return <Modal open portal onClose={onClose} title={t('inv.credit.dialogTitle', { number: inv.num })} max={620}
    footer={<React.Fragment><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button><Button variant="danger-solid" icon="files" disabled={busy || gross <= 0 || invalidDate} onClick={submitCredit} data-help={t('helpTopics.credit.body')} data-help-title={t('helpTopics.credit.title')}>{t('inv.credit.continue')}{gross > 0 ? ' · ' + formatKr(gross) : ''}</Button></React.Fragment>}>
    <div className="col gap-14" style={{ paddingBottom: 4 }}>
      <p className="t-body" style={{ margin: 0 }}>{splitAround('inv.credit.intro', 'customer', <b>{inv.customer}</b>)}</p>
      {/* En faktura kan delkrediteres flere ganger. Da er «full kreditering» resten,
          ikke fakturaen om igjen — og det maa staa der brukeren velger. */}
      {partiallyCredited && <div className="alert alert-info"><span className="alert-ico"><Icon name="info" size={18} /></span><span>{t('inv.credit.alreadyCredited', { amount: formatKr(allLines.reduce((sum, line) => sum + line.creditedGross, 0)) })}{closedLines > 0 ? ' ' + t('inv.credit.closedLines', { n: closedLines }) : ''}</span></div>}
      {Number(inv.paidAmount) > 0.005 && <div className="alert alert-warning"><span className="alert-ico"><Icon name="alertTri" size={18} /></span><span>{t('inv.credit.hasPayments')}</span></div>}
      <Field label={t('inv.credit.date')} hint={invalidDate ? t('inv.credit.dateBeforeInvoice') : t('inv.credit.dateHint')}><Input type="date" min={minimumCreditDate} value={creditDate} onChange={(event) => setCreditDate(event.target.value)} /></Field>
      <div className="seg" style={{ display: 'inline-flex', alignSelf: 'flex-start' }}>
        <button className={mode === 'full' ? 'active' : ''} onClick={() => setMode('full')}><Icon name="files" size={16} />{partiallyCredited ? t('inv.credit.rest') : t('inv.credit.full')}</button>
        <button className={mode === 'partial' ? 'active' : ''} onClick={() => setMode('partial')}><Icon name="sliders" size={16} />{t('inv.credit.partial')}</button>
      </div>
      {mode === 'partial' && <div className="cn-lines">
        <div className="cn-lines-hd"><span style={{ flex: 'none', width: 26 }} /><span style={{ flex: 1 }}>{t('inv.credit.line')}</span><span style={{ width: 78, textAlign: 'center' }} data-help={t('helpTopics.quantity.body')} data-help-title={t('helpTopics.quantity.title')}>{t('inv.credit.count')}</span><span style={{ width: 120, textAlign: 'right' }}>{t('inv.credit.credited')}</span></div>
        {sourceLines.map((line, index) => {
          const checked = selected[index];
          const row = credits[index];
          return <div key={line.sourceLineId || index} className={'cn-line' + (checked ? '' : ' off')}>
            <button type="button" className={'inbox-check' + (checked ? ' on' : '')} style={{ flex: 'none' }} onClick={() => setSelected((current) => current.map((value, i) => i === index ? !value : value))}>{checked && <Icon name="check" size={13} stroke={3} />}</button>
            <span style={{ flex: 1, minWidth: 0 }}><span className="t-sm" style={{ fontWeight: 700, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line.description || t('inv.fallback.line', { n: index + 1 })}</span><span className="t-xs muted" style={{ fontWeight: 600 }}>{line.remainingQuantity} × {formatKr(line.unitPrice)}{line.creditedGross > 0.005 ? ' · ' + t('inv.credit.lineAlready', { amount: formatKr(line.creditedGross) }) : ''}</span></span>
            <span style={{ width: 78, flex: 'none' }}><Input inputMode="decimal" disabled={!checked} value={quantities[index]} onChange={(event) => setQuantities((current) => current.map((value, i) => i === index ? event.target.value : value))} style={{ textAlign: 'center', padding: '7px 8px' }} /></span>
            <span style={{ width: 120, flex: 'none' }}><Input inputMode="decimal" disabled={!checked} value={amounts[index]} onChange={(event) => setAmounts((current) => current.map((value, i) => i === index ? window.PlummeMoneyInput.normalizeEditable(event.target.value,{previousValue:value}) : value))} onBlur={() => setAmounts((current) => current.map((value, i) => i === index ? String(Math.min(row.maxGross, Math.max(0, Math.round(parseAmount(value))))) : value))} style={{ textAlign: 'right', padding: '7px 8px' }} /></span>
          </div>;
        })}
      </div>}
      <div className="cn-total"><span className="t-sm" style={{ fontWeight: 700 }}>{t('inv.credit.total')}</span><div style={{ textAlign: 'right' }}><b className="t-num" style={{ fontSize: 18 }}>{formatKr(gross)}</b><div className="t-xs muted" style={{ fontWeight: 600 }}>{t('inv.credit.breakdown', { net: formatKr(net), vat: formatKr(gross - net) })}{mode === 'partial' ? t('inv.credit.partialSuffix') : ''}</div></div></div>
    </div>
  </Modal>;
}
function SalesShareBar({ pct }) {
  return <div style={{ position: 'relative', background: 'var(--panel-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-pill)', height: 22, minWidth: 130, overflow: 'hidden' }}>
    <div style={{ width: Math.max(pct, 6) + '%', height: '100%', background: 'linear-gradient(180deg,var(--plum-600),var(--plum-700))' }} />
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: pct >= 45 ? '#fff' : 'var(--plum-700)' }}>{pct} %</div>
  </div>;
}

const SALES_TILE_TINTS = {
  plum: { bg: 'var(--plum-50)', bd: 'var(--plum-200)' },
  green: { bg: 'var(--success-bg)', bd: 'var(--success-bd)' },
  peach: { bg: 'var(--warning-bg)', bd: 'var(--warning-bd)' },
  blue: { bg: 'color-mix(in srgb, #3b82f6 10%, var(--card))', bd: 'color-mix(in srgb, #3b82f6 28%, var(--card))' },
};

function SalesTile({ value, label, tint = 'plum' }) {
  const t = SALES_TILE_TINTS[tint] || SALES_TILE_TINTS.plum;
  return <div style={{ background: t.bg, border: '1px solid ' + t.bd, borderRadius: 'var(--r-md)', padding: '18px 16px', textAlign: 'center' }}>
    <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1 }}>{value}</div>
    <div className="t-sm muted" style={{ fontWeight: 700, marginTop: 4 }}>{label}</div>
  </div>;
}

function SalesReport({ rows, filter, setRoute, filterEl }) {
  const [sub, setSub] = useState('kunde');
  const invoices = (rows || [])
    .filter((invoice) => invoice.type === 'faktura' && invoice.status !== 'utkast')
    .filter((invoice) => dateInFilter(invoice.date, filter))
    .filter((invoice) => amountInFilter(invoice.net, filter));
  const customerMap = new Map();
  invoices.forEach((invoice) => {
    const key = invoice.customer || t('inv.report.unknownCustomer');
    const current = customerMap.get(key) || { name: key, antall: 0, net: 0 };
    current.antall += 1;
    current.net += Number(invoice.net) || 0;
    customerMap.set(key, current);
  });
  const customerRows = [...customerMap.values()].sort((a, b) => b.net - a.net);
  const productMap = new Map();
  invoices.forEach((invoice) => {
    const lines = Array.isArray(invoice._backend?.lines) ? invoice._backend.lines : [];
    if (!lines.length) {
      const key = t('inv.report.unspecifiedSale');
      const current = productMap.get(key) || { name: key, antall: 0, net: 0 };
      current.antall += 1;
      current.net += Number(invoice.net) || 0;
      productMap.set(key, current);
      return;
    }
    lines.forEach((line, index) => {
      const key = invoiceListLineLabel(line, index);
      const current = productMap.get(key) || { name: key, antall: 0, net: 0 };
      current.antall += Number(line.quantity ?? line.qty ?? 1) || 1;
      current.net += invoiceListLineNet(line);
      productMap.set(key, current);
    });
  });
  const productRows = [...productMap.values()].filter((row) => row.net > 0).sort((a, b) => b.net - a.net);
  const customerTotal = customerRows.reduce((sum, row) => sum + row.net, 0);
  const productTotal = productRows.reduce((sum, row) => sum + row.net, 0);
  const productCount = productRows.reduce((sum, row) => sum + row.antall, 0);
  const pct = (value, total) => total > 0 ? Math.round(value / total * 100) : 0;
  const isCustomer = sub === 'kunde';
  const openCustomer = (name) => {
    window.__openCustomer = name;
    setRoute && setRoute('kunder');
  };
  return <div className="mt-20">
    <div className="row gap-10" style={{ marginBottom: 18, alignItems: 'center' }}>
      <div className="seg-mini">
        <button className={isCustomer ? 'active' : ''} onClick={() => setSub('kunde')}>{t('inv.report.customer')}</button>
        <button className={!isCustomer ? 'active' : ''} onClick={() => setSub('produkt')}>{t('inv.report.product')}</button>
      </div>
      {filterEl}
    </div>
    {isCustomer ? <React.Fragment>
      <div className="grid-4 gap-14" style={{ marginBottom: 10 }}>
        <SalesTile value={invoices.length} label={t('inv.report.tile.sales')} tint="plum" />
        <SalesTile value={customerRows.length} label={t('inv.report.tile.activeCustomers')} tint="green" />
        <SalesTile value={customerRows.filter((row) => row.antall <= 1).length} label={t('inv.report.tile.newCustomers')} tint="peach" />
        <SalesTile value={customerRows.length} label={t('inv.report.tile.totalCustomers')} tint="blue" />
      </div>
      <div className="grid-2 gap-14" style={{ marginBottom: 18 }}>
        <SalesTile value={fmt(customerTotal)} label={t('inv.report.tile.customerRevenue')} tint="green" />
        <SalesTile value={fmt(customerTotal)} label={t('inv.report.tile.totalRevenue')} tint="plum" />
      </div>
      <p className="t-sm muted" style={{ fontWeight: 600, marginBottom: 18 }}>{t(customerRows.length === 1 ? 'inv.report.customerSummaryOne' : 'inv.report.customerSummaryMany', { sales: invoices.length, customers: customerRows.length, amount: fmt(customerTotal) })}</p>
      <div className="tbl-wrap" style={{ border: 'none' }}><table className="tbl salesrpt-tbl">
        <thead><tr><th>{t('inv.report.customer')}</th><th style={{ textAlign: 'right' }}>{t('inv.report.salesCount')}</th><th style={{ textAlign: 'right' }}>{t('inv.report.exVat')}</th><th style={{ textAlign: 'right' }}>{t('inv.report.share')}</th></tr></thead>
        <tbody>{customerRows.length === 0 ? <tr><td colSpan={4}><div className="t-sm muted" style={{ padding: '14px 2px', fontWeight: 600 }}>{t('inv.report.noSales')}</div></td></tr> :
          customerRows.map((row) => <tr data-app-control="true" key={row.name} className="salesrpt-row" style={{ cursor: 'pointer' }} onClick={() => openCustomer(row.name)} title={t('inv.report.openCustomer', { name: row.name })}><td style={{ fontWeight: 700, color: 'var(--plum-700)' }}>{row.name}</td><td data-label={t('inv.report.salesCount')} style={{ textAlign: 'right' }}>{row.antall}</td><td data-label={t('inv.report.exVat')} style={{ textAlign: 'right' }} className="t-num">{fmt(row.net)}</td><td><div className="row" style={{ justifyContent: 'flex-end' }}><SalesShareBar pct={pct(row.net, customerTotal)} /></div></td></tr>)}</tbody>
        <tfoot><tr style={{ borderTop: '2px solid var(--border-strong)' }}><td style={{ fontWeight: 800 }}>{t('inv.report.total')} <span className="muted" style={{ fontWeight: 600 }}>{t(customerRows.length === 1 ? 'inv.report.totalCustomer' : 'inv.report.totalCustomers', { n: customerRows.length })}</span></td><td data-label={t('inv.report.salesCount')} style={{ textAlign: 'right', fontWeight: 800 }}>{invoices.length}</td><td data-label={t('inv.report.exVat')} style={{ textAlign: 'right', fontWeight: 800 }} className="t-num">{fmt(customerTotal)}</td><td /></tr></tfoot>
      </table></div>
    </React.Fragment> : <React.Fragment>
      <div className="grid-4 gap-14" style={{ marginBottom: 10 }}>
        <SalesTile value={productCount} label={t('inv.report.tile.soldProducts')} tint="plum" />
        <SalesTile value={productRows.length} label={t('inv.report.tile.productTypes')} tint="green" />
        <SalesTile value={fmt(productTotal)} label={t('inv.report.tile.productRevenue')} tint="peach" />
        <SalesTile value={fmt(productTotal)} label={t('inv.report.tile.totalRevenue')} tint="blue" />
      </div>
      <p className="t-sm muted" style={{ fontWeight: 600, marginBottom: 18 }}>{t(productRows.length === 1 ? 'inv.report.productSummaryOne' : 'inv.report.productSummaryMany', { count: productCount, types: productRows.length, amount: fmt(productTotal) })}</p>
      <div className="tbl-wrap" style={{ border: 'none' }}><table className="tbl salesrpt-tbl">
        <thead><tr><th>{t('inv.report.product')}</th><th style={{ textAlign: 'right' }} data-help={t('helpTopics.quantity.body')} data-help-title={t('helpTopics.quantity.title')}>{t('inv.report.count')}</th><th style={{ textAlign: 'right' }}>{t('inv.report.exVat')}</th><th style={{ textAlign: 'right' }}>{t('inv.report.share')}</th></tr></thead>
        <tbody>{productRows.length === 0 ? <tr><td colSpan={4}><div className="t-sm muted" style={{ padding: '14px 2px', fontWeight: 600 }}>{t('inv.report.noProducts')}</div></td></tr> :
          productRows.map((row) => <tr key={row.name}><td style={{ fontWeight: 700, color: 'var(--plum-700)' }}>{row.name}</td><td data-label={t('inv.report.count')} style={{ textAlign: 'right' }}>{row.antall}</td><td data-label={t('inv.report.exVat')} style={{ textAlign: 'right' }} className="t-num">{fmt(row.net)}</td><td><div className="row" style={{ justifyContent: 'flex-end' }}><SalesShareBar pct={pct(row.net, productTotal)} /></div></td></tr>)}</tbody>
        <tfoot><tr style={{ borderTop: '2px solid var(--border-strong)' }}><td style={{ fontWeight: 800 }}>{t('inv.report.total')} <span className="muted" style={{ fontWeight: 600 }}>{t(productRows.length === 1 ? 'inv.report.totalProduct' : 'inv.report.totalProducts', { n: productRows.length })}</span></td><td data-label={t('inv.report.count')} style={{ textAlign: 'right', fontWeight: 800 }}>{productCount}</td><td data-label={t('inv.report.exVat')} style={{ textAlign: 'right', fontWeight: 800 }} className="t-num">{fmt(productTotal)}</td><td /></tr></tfoot>
      </table></div>
    </React.Fragment>}
  </div>;
}

function copyText(value, successText) {
  const fallback = () => {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };
  try {
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(value).then(() => toast(successText, 'success')).catch(() => { fallback(); toast(successText, 'success'); });
    else { fallback(); toast(successText, 'success'); }
  } catch (e) {
    fallback();
    toast(successText, 'success');
  }
}

function invoiceListTodayIso() {
  return new Date().toISOString().slice(0, 10);
}

function invoiceListDateToIso(value, fallback = invoiceListTodayIso()) {
  const text = String(value || '').trim();
  const nb = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (nb) return `${nb[3]}-${nb[2].padStart(2, '0')}-${nb[1].padStart(2, '0')}`;
  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;
  return fallback;
}
function invoiceListCreditMinimumDate(invoice) { return invoiceListDateToIso(invoice?._backend?.date || invoice?.date); }
function invoiceListIsoToNb(value) {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : text;
}

function invoiceListNumber(value) {
  const parsed = Number(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
}

function mapImportedInvoiceHistoryRow(row) {
  const isCredit = row?.documentKind === 'creditNote';
  const amount = (isCredit ? -1 : 1) * Math.abs(Number(row?.grossAmount || 0));
  const net = (isCredit ? -1 : 1) * Math.abs(Number(row?.netAmount || 0));
  const dueIso = String(row?.dueDate || '');
  const paid = row?.status === 'paid';
  const status = isCredit ? 'kreditert' : paid ? 'betalt' : dueIso && dueIso < invoiceListTodayIso() ? 'forfalt' : 'sendt';
  return {
    id: `imported-${row.id || row.sourceKey || row.invoiceNumber}`,
    num: row.invoiceNumber || '',
    type: row.documentKind === 'creditNote' ? 'kreditnota' : 'faktura',
    status,
    paymentStatus: paid ? 'paid' : 'unpaid',
    customer: row.customerName || t('inv.report.unknownCustomer'),
    date: invoiceListIsoToNb(row.date),
    due: invoiceListIsoToNb(row.dueDate),
    net,
    amount,
    importedReadOnly: true,
    importDocumentKey: row.sourceKey || row.id,
    importStatusOverride: row.statusOverride || null,
    creditedFromInvoiceNumber: row.creditedInvoiceNumber || '',
    importedNote: row.documentKind === 'creditNote' && row.creditedInvoiceNumber
      ? t('inv.importHistory.creditReference', { number: row.creditedInvoiceNumber })
      : t('inv.importHistory.sourceNote'),
    _backend: row,
  };
}

function mapImportedInvoiceHistoryRows(rows) {
  const mapped = (rows || []).map(mapImportedInvoiceHistoryRow);
  const creditsByInvoiceNumber = new Map(mapped
    .filter((invoice) => invoice.type === 'kreditnota' && invoice.creditedFromInvoiceNumber)
    .map((credit) => [String(credit.creditedFromInvoiceNumber), credit]));
  return mapped.map((invoice) => {
    const credit = invoice.type === 'faktura' ? creditsByInvoiceNumber.get(String(invoice.num || '')) : null;
    return credit ? {
      ...invoice,
      status: 'kreditert',
      fullyCredited: true,
      relatedImportedCreditNoteNumber: credit.num,
      importedNote: t('inv.importHistory.creditedReference', { number: credit.num }),
    } : invoice;
  });
}

function ImportedInvoiceDetail({ inv, onStatusChange }) {
  const [busy, setBusy] = React.useState('');
  const manuallySettled = Boolean(inv.importStatusOverride?.status === 'settled');
  const canSettle = inv.paymentStatus !== 'paid' && inv.importDocumentKey;
  const mutateStatus = async (action) => {
    if (!inv.importDocumentKey || !window.PlummeBackend?.settleAccountingImportSalesHistoryDocument) return;
    setBusy(action);
    try {
      await window.PlummeBackend.settleAccountingImportSalesHistoryDocument({
        action,
        documentKey: inv.importDocumentKey,
        settledAt: invoiceListTodayIso(),
        note: action === 'settle' ? 'Markert som oppgjort fra fakturalisten.' : '',
      });
      toast(action === 'settle' ? t('inv.importHistory.settledToast') : t('inv.importHistory.reopenedToast'), 'success');
      onStatusChange?.();
    } catch (error) {
      toast(error?.message || t('inv.importHistory.statusError'), 'error');
    } finally {
      setBusy('');
    }
  };
  const actions = [
    ['eye', t('inv.act.view.invoice')],
    ['copy', t('inv.act.copy')],
    ['box', t('inv.act.packingSlip')],
    ['download', t('inv.act.download')],
    ['link', t('inv.act.copyLink')],
    ['send', t('inv.act.sendAgain')],
    ['files', t('inv.act.credit')],
  ];
  return <div className="fak-detail">
    <div className="bg-note info"><Icon name="archive" size={18} /><span>{inv.importedNote}</span></div>
    <div className="grid-2 gap-14 mt-14">
      <div className="fak-box">
        <div className="t-sm muted">{t('inv.col.customer')}</div>
        <div className="t-h3" style={{ fontSize: 16, marginTop: 3 }}>{inv.customer}</div>
      </div>
      <div className="fak-box">
        <div className="t-sm muted">{t('inv.detail.paymentStatus')}</div>
        <span className={'pp ' + (inv.paymentStatus === 'paid' ? 'pp-paid' : 'pp-unpaid')}>{t(inv.paymentStatus === 'paid' ? 'inv.status.paid' : 'inv.status.unpaid')}</span>
        {manuallySettled && <div className="t-xs muted" style={{ marginTop: 6, fontWeight: 700 }}>{t('inv.importHistory.manualSettledNote')}</div>}
      </div>
    </div>
    <div className="fak-box mt-14">
      <div className="t-h3" style={{ fontSize: 14, marginBottom: 8 }}>{t('inv.detail.amountTitle')}</div>
      <div className="kv">
        <div className="kv-row"><span className="kv-key">{t('inv.detail.net')}</span><span className="kv-val">{fmt(inv.net)}</span></div>
        <div className="kv-row"><span className="kv-key" style={{ fontWeight: 900 }}>{t('inv.detail.totalAmount')}</span><span className="kv-val" style={{ fontWeight: 900 }}>{fmt(inv.amount)}</span></div>
      </div>
    </div>
    <div className="row gap-10 mt-16 wrap fak-acts-inline">
      {canSettle && <Button size="sm" icon="check" disabled={Boolean(busy)} onClick={() => void mutateStatus('settle')}>{busy === 'settle' ? t('common.saving') : t('inv.importHistory.markSettled')}</Button>}
      {manuallySettled && <Button size="sm" variant="ghost" icon="refresh" disabled={Boolean(busy)} onClick={() => void mutateStatus('reopen')}>{busy === 'reopen' ? t('common.saving') : t('inv.importHistory.reopenStatus')}</Button>}
      {actions.map(([icon, label]) => <Button key={label} size="sm" variant="secondary" icon={icon} disabled>{label}</Button>)}
    </div>
  </div>;
}

function PakkseddelDialog({ inv, onClose, onPreview, onDownload, busy }) {
  const [shippingDate, setShippingDate] = useState(() => invoiceListTodayIso());
  const invoiceDateIso = invoiceListDateToIso(inv.date);
  return <Modal open portal onClose={onClose} title={t('inv.act.packingSlip')} max={520}
    footer={<React.Fragment>
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button variant="secondary" icon="eye" disabled={Boolean(busy)} onClick={() => onPreview(shippingDate)}>{t('inv.packing.preview')}</Button>
      <Button icon="download" disabled={Boolean(busy)} onClick={() => onDownload(shippingDate)}>{t('inv.packing.download')}</Button>
    </React.Fragment>}>
    <p className="muted" style={{ fontWeight: 600, marginBottom: 14 }}>{t('inv.packing.intro')}</p>
    <Field label={t('inv.packing.shippingDate')}>
      <Input type="date" value={shippingDate} onChange={(e) => setShippingDate(e.target.value)} />
    </Field>
    <div className="row gap-10 mt-10">
      <Button size="sm" variant="ghost" onClick={() => setShippingDate(invoiceDateIso)} data-help={t('helpTopics.invoiceDate.body')} data-help-title={t('helpTopics.invoiceDate.title')}>{t('inv.packing.invoiceDate')}</Button>
      <Button size="sm" variant="ghost" onClick={() => setShippingDate(invoiceListTodayIso())}>{t('ui.today')}</Button>
    </div>
    <p className="t-xs muted" style={{ fontWeight: 600, marginTop: 12 }}>{t('inv.packing.contents')}</p>
  </Modal>;
}

function FakturaDetail({ inv, setRoute, paymentAccounts }) {
  const [dlg, setDlg] = useState(null);
  const [menu, setMenu] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [pdfPreview, setPdfPreview] = useState(null);
  const [paymentEditor, setPaymentEditor] = useState(null);
  const [paymentTargetId, setPaymentTargetId] = useState(''); const [assistantCreditSelections,setAssistantCreditSelections]=useState(null);
  const [overpaymentForm, setOverpaymentForm] = useState({ sourceInvoiceId: '', amount: '', date: invoiceListTodayIso(), settlementAccountCode: '1920' });
  const [linkPostingOpen, setLinkPostingOpen] = useState(false);
  // Pengene kommer inn paa kontoen kunden ble bedt om aa betale til ("Betal til" paa
  // fakturaen), saa den er standard oppgjorskonto her.
  const defaultSettlementAccountCode = () => String(inv.bankAccountCode || inv._backend?.bankAccountCode || '').trim() || paymentAccounts?.[0]?.value || '1920';
  const [payForm, setPayForm] = useState(() => ({
    amount: String(inv.remainingAmount || Math.abs(inv.amount) || ''),
    date: new Date().toISOString().slice(0, 10),
    settlementAccountCode: defaultSettlementAccountCode(),
    settlementKind: 'payment',
    feeAmount: '',
    feeAccountCode: '7770',
    note: '',
  }));
  const sendRecipients = typeof invoiceSendRecipientOptions === 'function' ? invoiceSendRecipientOptions(inv._backend || inv) : [];
  // Reuse all initial send fields when resetting the dialog, including delivery mode.
  const initialSendForm = () => {
    const initialEmail = invoiceListCustomerEmail(inv);
    const matched = sendRecipients.find((recipient) => recipient.email === initialEmail) || sendRecipients[0];
    return {
      deliveryMode: 'attachment',
      recipientId: matched?.id || '__new__',
      email: matched?.email || initialEmail,
      saveContact: false,
      contactName: '',
      contactRole: '',
      subject: invoiceListSendSubject(inv),
    };
  };
  const [sendForm, setSendForm] = useState(initialSendForm);
  const payments = inv.payments || []; const displayedPayments = [...payments, ...(inv.relatedCreditNotes || []).flatMap((credit) => credit.payments || [])];
  const betalt = inv.paidAmount || 0;
  const rest = Math.max(0, inv.remainingAmount ?? Math.abs(inv.amount) - betalt);
  const overpaid = Math.max(0, Number(inv.overpaidAmount ?? (betalt - Math.abs(inv.amount))));
  const paid = inv.paymentStatus === 'paid';
  const noPayment = inv.type === 'ordre' || inv.type === 'tilbud';
  // En kreditnota gjør opp seg selv mot fakturaen den krediterer. Bare den delen som
  // ikke er motregnet kan betales tilbake, og pengene går ut av kassen — derfor egne
  // ord og motsatt fortegn gjennom hele oppgjørsboksen.
  const isCredit = inv.type === 'kreditnota';
  const paymentIsRefund = isCredit || Boolean(paymentTargetId) || paymentEditor?.direction === 'outgoing';
  const relatedRefundTarget = !isCredit
    ? (inv.relatedCreditNotes || []).find((credit) => Number(credit.remainingAmount) > 0.005)
    : null;
  const customerRefundable = Number(inv.refundableAmount ?? (overpaid + (inv.relatedCreditNotes || []).reduce((sum, credit) => sum + Math.max(0, Number(credit.remainingAmount) || 0), 0)));
  const offsetAmount = inv.offsetAmount || 0;
  const refundable = isCredit ? (inv.refundableAmount || 0) : 0;
  const settlementChip = !isCredit
    ? { label: overpaid > 0.005 ? t('inv.detail.overpaid') : paid ? t('inv.status.paid') : betalt > 0 ? t('inv.detail.partiallyPaid') : t('inv.detail.unpaid'), settled: paid }
    : inv.settlementMode === 'offset' ? { label: t('inv.detail.creditOffset'), settled: true }
    : inv.settlementMode === 'refunded' ? { label: t('inv.detail.creditRefunded'), settled: true }
    : { label: t('inv.detail.creditOpen'), settled: false };
  const offsetText = inv.creditedFromInvoiceNumber
    ? t('inv.detail.offsetAgainst', { document: inv.creditedFromInvoiceNumber })
    : t('inv.detail.offsetAgainstInvoice');
  const canPay = Math.abs(inv.amount) > 0 && inv.status !== 'utkast' && !noPayment && (!isCredit || refundable > 0);
  const mva = inv.amount - inv.net;
  const mvaPct = inv.net ? Math.round(mva / inv.net * 100) : 25;
  const paymentOptions = paymentAccounts?.length ? paymentAccounts : [{ value: '1920', label: t('inv.pay.account.1920') }, { value: '2063', label: t('inv.pay.account.2063') }, { value: '1900', label: t('inv.pay.account.1900') }];
  const canArchive = inv.type === 'tilbud' || inv.type === 'ordre';
  const paymentAccountLabel = (code) => paymentOptions.find((account) => String(account.value) === String(code || ''))?.label || code || t('inv.unknownAccount');
  const foreignPayment = Boolean(inv.currency && inv.accountingCurrency && inv.currency !== inv.accountingCurrency);
  const settlementPreview=window.PlummeCurrencySettlementPreview.usePreview({enabled:dlg==='pay'&&foreignPayment&&!paymentIsRefund,documentType:'invoice',documentId:inv.id,paymentId:paymentEditor?.id,date:payForm.date,units:invoiceListNumber(payForm.originalAmount),bank:invoiceListNumber(payForm.bankAmount)});
  const settlementCarrying=settlementPreview.result?.carrying??invoiceListNumber(payForm.originalAmount)*Number(inv.exchangeRate||0);
  const foreignRemaining = foreignPayment && Math.abs(inv.amount) > 0.005 ? Math.round((Number(inv.originalTotalGross || 0) * rest / Math.abs(inv.amount)) * 100) / 100 : 0;

  useEffect(() => () => {
    if (pdfPreview?.objectUrl && pdfPreview.src) URL.revokeObjectURL(pdfPreview.src);
  }, [pdfPreview?.src]);

  useEffect(() => setPayForm((current) => ({ ...current, amount: String(rest || ''), originalAmount: String(foreignRemaining || ''), bankAmount: String(rest || '') })), [rest, foreignRemaining, inv.id]);

  // Nullstilles bare naar det er et annet dokument som staar i detaljen. `sentAt` laa
  // her for, og den settes midt i sendingen: skjemaet ble dermed nullstilt mens
  // dialogen sto aapen, saa leveringsmaaten og adressen brukeren nettopp skrev inn
  // forsvant.
  useEffect(() => {
    setSendForm(initialSendForm());
  }, [inv.id, inv.num]);

  const runAction = async (key, action, fallback = t('inv.err.actionFailed')) => {
    if (busyAction) return;
    setBusyAction(key);
    try {
      await action();
    } catch (error) {
      toast(error?.message || fallback, 'error');
    } finally {
      setBusyAction('');
    }
  };

  const openPaymentDialog = (payment = null, targetCredit = null) => {
    setPaymentEditor(payment);
    setPaymentTargetId(targetCredit?.id || '');
    const targetRemaining = targetCredit?.remainingAmount || rest;
    setPayForm({ id: crypto.randomUUID(), amount: String(payment ? payment.amount : (targetRemaining || '')), originalAmount: String(payment?.originalAmount || (foreignPayment ? foreignRemaining : '')), bankAmount: String(payment?.bankAmount || (targetRemaining || '')), date: payment?.iso || invoiceListTodayIso(), settlementAccountCode: payment?.account && payment.account !== 'Uten betaling' ? String(payment.account) : defaultSettlementAccountCode(), settlementKind: payment?.settlementKind || 'payment', feeAmount: payment?.feeAmount ? String(payment.feeAmount) : '', feeAccountCode: payment?.feeAccountCode || '7770', note: payment?.note || '' });
    setDlg('pay');
  }; useInvoiceListNativeAction({invoiceId:inv.id,payments,setDialog:setDlg,openPayment:openPaymentDialog,setPayment:setPayForm,setSend:setSendForm,setCreditSelections:setAssistantCreditSelections});

  const openPdfPreview = async () => {
    await runAction('view', async () => {
      setPdfPreview({ loading: true, name: inv.num || t('inv.type.invoice') });
      if (!window.PlummeBackend?.getInvoicePdfPreview) throw new Error(t('inv.err.pdfPreviewNotConnected'));
      try { const preview = await window.PlummeBackend.getInvoicePdfPreview(inv); setPdfPreview({ ...preview, title: inv.num || preview.name || t('inv.type.invoice') }); }
      catch (error) { setPdfPreview({ error: error?.message || t('inv.err.openDoc'), name: inv.num || t('inv.type.invoice') }); throw error; }
    }, t('inv.err.openDoc'));
  };

  const previewPackingSlip = (shippingDate) => runAction('packing-preview', async () => {
    setPdfPreview({ loading: true, name: t('inv.packing.title', { number: inv.num || '' }).trim() });
    if (!window.PlummeBackend?.getPackingSlipPdfPreview) throw new Error(t('inv.err.packingNotConnected'));
    const preview = await window.PlummeBackend.getPackingSlipPdfPreview(inv, { shippingDate });
    setPdfPreview({ ...preview, title: t('inv.packing.title', { number: inv.num || preview.name || '' }).trim() });
    setDlg(null);
  }, t('inv.err.previewPacking'));

  const downloadPackingSlip = (shippingDate) => runAction('packing-download', async () => {
    if (!window.PlummeBackend?.downloadPackingSlip) throw new Error(t('inv.err.packingNotConnected'));
    await window.PlummeBackend.downloadPackingSlip(inv, { shippingDate });
    setDlg(null);
    toast(t('inv.toast.packingDownloading'), 'success');
  }, t('inv.err.packingDownloadFailed'));

  const savePayment = () => runAction(paymentEditor ? 'edit-payment' : 'pay', async () => {
    if (!settlementPreview.ready) throw new Error(settlementPreview.error||t('yearFx.previewRequired'));
    if (!window.PlummeBackend?.addInvoicePayment) throw new Error(t('inv.err.paymentNotConnected'));
    if (paymentEditor) {
      if (!window.PlummeBackend?.replaceInvoicePayment) throw new Error(t('inv.err.paymentEditNotConnected'));
    }
    const save = paymentEditor
      ? (input) => window.PlummeBackend.replaceInvoicePayment(paymentEditor.id, input)
      : (input) => window.PlummeBackend.addInvoicePayment(paymentTargetId || inv, input);
    await save({
      id: payForm.id, amount: Number(String(foreignPayment ? payForm.bankAmount : payForm.amount).replace(',', '.')), originalAmount: foreignPayment ? Number(String(payForm.originalAmount).replace(',', '.')) : undefined, bankAmount: foreignPayment ? Number(String(payForm.bankAmount).replace(',', '.')) : undefined,
      feeAmount: Number(String(payForm.feeAmount || 0).replace(',', '.')),
      feeAccountCode: payForm.feeAccountCode || '7770',
      date: payForm.date,
      settlementAccountCode: payForm.settlementAccountCode,
      settlementKind: payForm.settlementKind,
      note: payForm.note,
    });
    setDlg(null);
    setPaymentEditor(null);
    setPaymentTargetId('');
    toast(t(paymentEditor
      ? (paymentIsRefund ? 'inv.toast.refundUpdated' : 'inv.toast.paymentUpdated')
      : (paymentIsRefund ? 'inv.toast.refundRegistered' : 'inv.toast.paymentRegistered')), 'success');
  }, t(paymentEditor
    ? (paymentIsRefund ? 'inv.err.refundUpdateFailed' : 'inv.err.paymentUpdateFailed')
    : (paymentIsRefund ? 'inv.err.refundFailed' : 'inv.err.paymentFailed')));

  const offsetCustomerCredit = () => runAction('offset-credit', async () => {
    const credit = (inv.availableCreditNotes || [])[0];
    if (!credit || !window.PlummeBackend?.allocateCustomerCredit) throw new Error(t('inv.err.noCustomerCredit'));
    await window.PlummeBackend.allocateCustomerCredit({
      invoiceId: inv.id,
      creditNoteId: credit.id,
      amount: Math.min(rest, credit.remainingAmount),
      date: invoiceListTodayIso(),
    });
    toast(t('inv.toast.customerCreditOffset'), 'success');
  }, t('inv.err.customerCreditOffsetFailed'));

  const openOverpaymentRefund = () => {
    setOverpaymentForm({ id: crypto.randomUUID(), sourceInvoiceId: inv.id, amount: String(customerRefundable), feeAmount: '', feeAccountCode: '7770', date: invoiceListTodayIso(), settlementAccountCode: defaultSettlementAccountCode() });
    setDlg('overpayment-refund');
  };

  const openOverpaymentAllocation = () => {
    const source = (inv.availableCustomerOverpayments || [])[0];
    setOverpaymentForm({ sourceInvoiceId: source?.id || '', amount: String(Math.min(rest, Number(source?.amount) || 0)), date: invoiceListTodayIso(), settlementAccountCode: defaultSettlementAccountCode() });
    setDlg('overpayment-allocation');
  };

  const saveOverpaymentRefund = () => runAction('refund-overpayment', async () => {
    if (!window.PlummeBackend?.refundCustomerOverpayment) throw new Error(t('inv.overpayment.notConnected'));
    await window.PlummeBackend.refundCustomerOverpayment({
      invoiceId: inv.id, id: overpaymentForm.id,
      amount: invoiceListNumber(overpaymentForm.amount), feeAmount: invoiceListNumber(overpaymentForm.feeAmount), feeAccountCode: overpaymentForm.feeAccountCode,
      date: overpaymentForm.date,
      settlementAccountCode: overpaymentForm.settlementAccountCode,
      note: t('inv.overpayment.refundNote'),
    });
    setDlg(null);
    toast(t('inv.overpayment.refunded'), 'success');
  }, t('inv.overpayment.refundFailed'));

  const saveOverpaymentAllocation = () => runAction('allocate-overpayment', async () => {
    const source = (inv.availableCustomerOverpayments || []).find((entry) => entry.id === overpaymentForm.sourceInvoiceId);
    if (!source || !window.PlummeBackend?.allocateCustomerOverpayment) throw new Error(t('inv.overpayment.noSource'));
    await window.PlummeBackend.allocateCustomerOverpayment({
      invoiceId: inv.id,
      sourceInvoiceId: source.id,
      amount: invoiceListNumber(overpaymentForm.amount),
      date: overpaymentForm.date,
      note: t('inv.overpayment.allocationNote', { source: source.num }),
    });
    setDlg(null);
    toast(t('inv.overpayment.allocated'), 'success');
  }, t('inv.overpayment.allocationFailed'));

  const settleWithoutPayment = () => runAction('settle-without-payment', async () => {
    if (!window.PlummeBackend?.addInvoicePayment) throw new Error(t('inv.err.settleNotConnected'));
    await window.PlummeBackend.addInvoicePayment(inv, {
      amount: rest,
      date: payForm.date,
      settlementAccountCode: payForm.settlementAccountCode,
      settlementKind: 'no_payment',
      note: 'Oppgjort uten betaling',
    });
    setDlg(null);
    toast(t('inv.toast.settledWithoutPayment'), 'success');
  }, t('inv.err.settleFailed'));

  const sendDocument = () => runAction('send', async () => {
    if (!window.PlummeBackend?.sendInvoiceDocument) throw new Error(t('inv.err.sendNotConnected'));
    const email = String(sendForm.email || '').trim();
    if (!email) throw new Error(t('inv.err.needEmail'));
    // E-posttjenesten melder fra om avvisningen forst naar sendingen er i gang.
    // En skrivefeil som «kunde@firma» eller «kunde@bedriftø.no» stoppes her.
    const emailProblem = window.PlummeInvoiceDelivery.emailProblem(email);
    if (emailProblem === 'charset') {
      throw new Error(t('inv.err.emailCharset', 'E-postadresser kan ikke inneholde æ, ø, å eller andre spesialtegn.'));
    }
    if (emailProblem) {
      throw new Error(t('inv.err.invalidEmail', 'Dette ser ikke ut som en gyldig e-postadresse.'));
    }
    // Kontaktpersonen lagres foer sendingen, saa adressen er bevart selv om
    // e-postutsendingen skulle feile.
    await invoiceSaveRecipientContact({
      document: inv._backend || inv,
      recipient: { ...sendForm, email },
      onWarn: (message) => toast(message, 'warning'),
    });
    const payload = invoiceListSendPayload(inv, email);
    const options = {
      deliveryMode: sendForm.deliveryMode === 'link' ? 'link' : 'attachment',
      emailSubject: sendForm.subject,
    };
    setDlg(null);
    toast(t('inv.toast.sendingBackground', { document: documentLabelCap(inv) }), 'info');
    void window.PlummeBackend.sendInvoiceDocument(payload, options).then((result) => {
      // Uten e-post ble dette tidligere en dempet info-toast som leste som «sendt».
      // Det er en mislykket sending, og skal se ut som det.
      toast(result?.emailSent ? t('inv.toast.documentSent', { document: documentLabelCap(inv) }) : (result?.message || t('inv.toast.readyNoEmail')), result?.emailSent ? 'success' : 'warning');
    }).catch((error) => {
      toast(error?.message || t('inv.err.sendFailed'), 'error');
    });
  }, t('inv.err.sendFailed'));

  // Leveringsstatus for siste e-post. Tom for dokumenter som aldri er sendt.
  const delivery = inv.delivery || { status: '', label: '', message: '', to: '', at: '', failed: false };
  const canMarkDelivered = inv.status !== 'utkast'
    && !inv.deliveredExternallyAt
    && (!inv.sentAt || delivery.failed);

  // En kreditering er en hendelse paa fakturaen, ikke bare et mindre tall i
  // «Gjenstaar». Historikken sier derfor hvor mye som ble kreditert og med hvilken
  // kreditnota, og skiller delkreditering fra full kreditering.
  const creditHistory = (inv.relatedCreditNotes || []).map((credit) => {
    const full = Math.abs(inv.amount) > 0.005 && credit.amount >= Math.abs(inv.amount) - 0.005;
    return [t(credit.draft ? 'inv.history.creditDraft' : full ? 'inv.history.creditFull' : 'inv.history.creditPartial', {
      amount: fmt(credit.amount),
      document: credit.num || t('inv.history.creditNoNumber'),
    }), credit.date, credit.draft ? 'muted' : 'ok'];
  });

  const history = [
    ...displayedPayments.map((payment) => [t(payment.direction === 'outgoing' ? 'inv.toast.refundRegistered' : 'inv.toast.paymentRegistered'), payment.date, 'ok']),
    ...creditHistory,
    // Motregningen er ikke en betaling og har ingen egen postering, men den er det
    // som gjor opp kreditnotaen — den skal staa i sporet.
    ...(isCredit && offsetAmount > 0.005 ? [[offsetText + ': ' + fmt(-offsetAmount), (inv._backend?.date || '').slice(0, 10) || inv.date, 'ok']] : []),
    // Hvem som godtok tilbudet staar i historikken: kundens klikk i lenken og en
    // aksept registrert i appen er ikke det samme sporet.
    ...(inv.offerAcceptedAt ? [[t(inv.offerAcceptedSource === 'manual' ? 'inv.history.offerAcceptedManual' : 'inv.history.offerAcceptedCustomer'), inv.offerAcceptedAt.slice(0, 10), 'ok']] : []),
    ...(inv.orderConfirmationSentAt ? [[t('inv.history.orderSent'), inv.orderConfirmationSentAt.slice(0, 10), 'ok']] : []),
    ...(inv.deliveredExternallyAt ? [[t('inv.history.deliveredExternally'), inv.deliveredExternallyAt.slice(0, 10), 'ok']] : []),
    ...(inv.sentAt && (!inv.deliveredExternallyAt || delivery.status !== 'external') ? [[t('inv.history.documentSentTo', { document: documentLabelCap(inv), recipient: delivery.to || inv.customer }), inv.sentAt.slice(0, 10), 'ok']] : []),
    // Sendt er ikke det samme som framme. Kom e-posten i retur, skal historikken
    // si det — ikke bare «sendt til kunde».
    ...(delivery.label && !['sent', 'external'].includes(delivery.status) ? [[delivery.label + (delivery.message ? ` (${delivery.message})` : ''), (delivery.at || '').slice(0, 10), delivery.failed ? 'error' : 'ok']] : []),
    ...(inv.publicViewedAt ? [[t('inv.detail.openedFromLink'), inv.publicViewedAt.slice(0, 10), 'ok']] : []),
    [t('inv.history.documentCreated'), inv.createdAt ? inv.createdAt.slice(0, 10) : inv.date, 'muted'],
  ];

  const offerConversion = window.PlummeOfferConversion;
  const converted = offerConversion.conversionState(inv.convertedDocuments || []);
  const alreadyConverted = converted.invoiced || converted.confirmed || converted.hasDraft;

  const markAccepted = (accepted) => runAction('accept', async () => {
    if (!window.PlummeBackend?.markOfferAccepted) throw new Error(t('inv.err.acceptNotConnected', 'Aksept er ikke koblet til backend.'));
    await window.PlummeBackend.markOfferAccepted(inv, accepted);
    toast(accepted ? t('inv.toast.offerAccepted', 'Tilbudet er satt som akseptert') : t('inv.toast.offerAcceptanceRemoved', 'Aksepten er fjernet'), 'success');
  }, t('inv.err.acceptFailed', 'Kunne ikke oppdatere aksepten.'));

  const copyPublicLink = () => runAction('copy-link', async () => {
    if (!window.PlummeBackend?.getInvoicePublicLink) throw new Error(t('inv.err.linkNotConnected'));
    const link = await window.PlummeBackend.getInvoicePublicLink(inv);
    copyText(link, t('inv.toast.linkCopied', { number: inv.num }));
  }, t('inv.err.linkFailed'));

  const downloadDocument = () => runAction('download', async () => {
    if (!window.PlummeBackend?.downloadInvoice) throw new Error(t('inv.err.downloadNotConnected'));
    await window.PlummeBackend.downloadInvoice(inv);
    toast(t('inv.toast.documentDownloading'), 'success');
  }, t('inv.err.downloadFailed'));
  // Konverteringen lager et utkast og aapner det i salgsflyten. Belop, dato og
  // linjer skal kunne justeres for dokumentet faktisk opprettes.
  const createFromOffer = (target) => runAction('convert-' + target, async () => {
    if ((target === 'ordre' ? converted.confirmed : converted.invoiced) || converted.hasDraft) throw new Error(t('inv.err.offerAlreadyConverted', 'Tilbudet har allerede et dokument eller utkast fra denne konverteringen.'));
    if (!window.PlummeBackend?.documentDraftFromOffer) throw new Error(t('inv.err.convertNotConnected', 'Konvertering er ikke koblet til backend.'));
    const created = window.PlummeBackend.documentDraftFromOffer(inv, target);
    const draft = invoiceListOpenDraftPayload(created);
    if (!draft) throw new Error(t('inv.err.convertOpenFailed', 'Utkastet ble laget, men kunne ikke åpnes.'));
    window.PlummeBackend?.setOpenInvoice?.(draft);
    toast(target === 'ordre'
      ? t('inv.toast.orderDraftFromOffer', 'Ordrebekreftelsen er åpnet — lagre den som utkast eller opprett den')
      : t('inv.toast.invoiceDraftFromOffer', 'Fakturaen er åpnet — lagre den som utkast eller opprett den'), 'success');
    setRoute && setRoute('faktura');
  }, t('inv.err.convertFailed', 'Kunne ikke opprette dokumentet fra tilbudet.'));

  const acts = [
    inv.status === 'utkast' && { label: t('inv.act.open'), icon: 'pencil', help: t('inv.act.openHelp'), onClick: () => { window.PlummeBackend?.setOpenInvoice?.(inv); setRoute && setRoute('faktura'); } },
    inv.status === 'utkast' && { label: t('inv.bulk.mode.create'), icon: 'fileText', variant: 'secondary', onClick: () => setDlg('issueOnly') },
    inv.status === 'utkast' && { label: t('inv.bulk.issue'), icon: 'send', variant: 'primary', onClick: () => setDlg('issue') },
    inv.type === 'tilbud' && inv.status !== 'utkast' && !alreadyConverted && { label: t('inv.act.convertOffer', 'Opprett'), icon: 'fileText', variant: 'primary', help: t('inv.act.convertOfferHelp', 'Lager en faktura eller ordrebekreftelse som utkast, med kunden og linjene fra tilbudet.'), onClick: () => setDlg('convert') },
    inv.status !== 'utkast' && { label: sendActionLabel(inv), icon: 'send', variant: 'primary', help: t('inv.act.sendHelp'), onClick: () => setDlg('send') },
    canMarkDelivered && { label: t('inv.act.markDelivered'), icon: 'check', variant: 'secondary', help: t('inv.act.markDeliveredHelp'), onClick: () => setDlg('deliver') },
    { label: t(PlummeI18n.keyFor('inv.act.view', inv.type, 'documentType'), t('inv.act.view.invoice')), icon: 'eye', variant: 'secondary', help: t('inv.act.viewHelp'), onClick: openPdfPreview },
    // Kopier lenke, last ned og pakkseddel er samme aerend — faa dokumentet ut av
    // appen — og tok tre plasser i handlingsraden. De ligger na bak «Del».
    inv.status !== 'utkast' && { label: t('inv.act.share', 'Del'), icon: 'link', variant: 'secondary', help: t('inv.act.shareHelp', 'Kopier delingslenke, last ned PDF eller lag pakkseddel.'), onClick: () => setDlg('share') },
    // Open an unsaved copy so saving it creates only one draft.
    inv.type !== 'kreditnota' && { label: t('inv.act.copy'), icon: 'copy', variant: 'secondary', help: t('inv.act.copyHelp'), onClick: () => runAction('copy', async () => {
      if (!window.PlummeBackend?.copyInvoiceForEditor) throw new Error(t('inv.err.copyNotConnected'));
      const copied = window.PlummeBackend.copyInvoiceForEditor(inv);
      const draft = invoiceListOpenDraftPayload(copied);
      if (!draft) throw new Error(t('inv.err.copyOpenFailed'));
      window.PlummeBackend?.setOpenInvoice?.(draft);
      toast(t('inv.toast.copyOpened', 'Kopien er åpnet — lagre den som utkast eller opprett den'), 'success');
      setRoute && setRoute('faktura');
    }, t('inv.err.copyFailed')) },
    canArchive && { label: t(inv.archivedAt ? 'inv.act.unarchive' : 'inv.act.archive'), icon: 'box', variant: 'secondary', help: t('inv.act.archiveHelp'), onClick: () => runAction('archive', async () => {
      if (!window.PlummeBackend?.archiveInvoice) throw new Error(t('inv.err.archiveNotConnected'));
      await window.PlummeBackend.archiveInvoice(inv, !inv.archivedAt);
      toast(t(inv.archivedAt ? 'inv.toast.archiveRestored' : 'inv.toast.archived'), 'success');
    }, t('inv.err.archiveFailed')) },
    inv.type === 'faktura' && inv.status !== 'utkast' && !inv.fullyCredited && { label: t('inv.act.credit'), icon: 'files', variant: 'danger', help: t('inv.act.creditHelp'), onClick: () => setDlg('credit') },
    inv.status === 'utkast' && { label: t('inv.act.delete'), icon: 'trash', variant: 'danger', help: t('inv.act.deleteHelp'), onClick: () => setDlg('delete') },
  ].filter(Boolean);

  return <div className="fak-detail">
    <div className="grid-2 gap-14">
      <div className="fak-box" data-help-title={t('inv.detail.dueTitle')} data-help={t('inv.detail.dueHelp')}>
        <div className="t-sm muted">{t(inv.type === 'tilbud' ? 'inv.detail.validUntil' : 'inv.detail.dueDate')}</div>
        <div className="t-h3" style={{ fontSize: 16, marginTop: 3, color: inv.status === 'forfalt' && !paid ? 'var(--error)' : 'var(--ink)' }}>{inv.due || '-'}</div>
      </div>
      <div className="fak-box" data-help-title={t('inv.col.status')} data-help={t('inv.detail.statusHelp')}>
        {inv.status === 'utkast' ? null : noPayment ? <React.Fragment>
          <div className="t-sm muted" style={{ fontWeight: 700, marginBottom: 8 }}>{t('inv.detail.completion')}</div>
          <div className="row between"><span className="t-sm muted">{t('inv.col.status')}</span><b>{t(inv.status === 'akseptert' || inv.status === 'bekreftet' ? 'inv.detail.completed' : 'inv.detail.notCompleted')}</b></div>
          {inv.offerAcceptedAt && <div className="t-sm muted mt-8">{t(inv.offerAcceptedSource === 'manual' ? 'inv.detail.acceptedManual' : 'inv.detail.acceptedCustomer', { date: inv.offerAcceptedAt.slice(0, 10) })}</div>}
          {inv.orderConfirmationSentAt && <div className="t-sm muted mt-8">{t('inv.detail.orderSentAt', { date: inv.orderConfirmationSentAt.slice(0, 10) })}</div>}
          {/* Hva tilbudet har blitt til. Utkast telles med, slik at brukeren ikke
              lager faktura nummer to fordi den forste ligger uferdig. */}
          {(inv.convertedDocuments || []).length > 0 && <div className="t-sm mt-8" style={{ fontWeight: 700 }}>
            {inv.convertedDocuments.map((document) => <div key={document.id} className="row gap-8" style={{ alignItems: 'center' }}>
              <Icon name={document.isDraft ? 'pencil' : 'checkCircle'} size={14} className="muted" />
              <span>{document.isDraft ? `${t('inv.status.draft')} (${invoiceListTypeLabel(document.type)})` : `${invoiceListTypeLabel(document.type)} ${document.num || ''}`}</span>
            </div>)}
          </div>}
          {/* Aksept er en statusendring, ikke en dokumenthandling, og hoerer
              derfor her — ved siden av statusen den endrer. */}
          {offerConversion.canMarkAccepted(inv) && <Button size="sm" variant="secondary" icon="check" className="mt-8" disabled={Boolean(busyAction)}
            data-help-title={t('inv.act.markAccepted', 'Sett som akseptert')} data-help={t('inv.act.markAcceptedHelp')}
            onClick={() => markAccepted(true)}>{t('inv.act.markAccepted', 'Sett som akseptert')}</Button>}
          {offerConversion.canUndoAcceptance(inv) && <Button size="sm" variant="ghost" icon="refresh" className="mt-8" disabled={Boolean(busyAction)}
            data-help-title={t('inv.act.undoAccepted', 'Fjern aksept')} data-help={t('inv.act.undoAcceptedHelp')}
            onClick={() => markAccepted(false)}>{t('inv.act.undoAccepted', 'Fjern aksept')}</Button>}
        </React.Fragment> : <React.Fragment>
          <div className="row between" style={{ alignItems: 'center' }}><span className="t-sm muted" style={{ fontWeight: 700 }}>{t(isCredit ? 'inv.detail.settlementStatus' : 'inv.detail.paymentStatus')}</span>
            <span className={'pp ' + (settlementChip.settled ? 'pp-paid' : 'pp-unpaid')}>{settlementChip.label}</span></div>
          <div className="row gap-18 mt-8"><div><div className="t-xs muted">{t(isCredit ? 'inv.detail.refunded' : 'inv.detail.paid')}</div><b className="t-num">{fmt(isCredit ? -betalt : betalt)}</b></div><div><div className="t-xs muted">{t(isCredit ? 'inv.detail.toCredit' : 'inv.detail.remaining')}</div><b className="t-num">{fmt(isCredit ? -rest : rest)}</b></div></div>
          {isCredit && offsetAmount > 0 && <div className="t-xs muted mt-8" style={{ fontWeight: 600 }}>{offsetText}: {fmt(-offsetAmount)}</div>}
          {isCredit && inv.allocatedAmount > 0 && <div className="t-xs muted mt-8" style={{ fontWeight: 600 }}>{t('inv.detail.customerCreditAllocated')}: {fmt(-inv.allocatedAmount)}</div>}
          {!isCredit && inv.allocatedCreditAmount > 0 && <div className="t-xs muted mt-8" style={{ fontWeight: 600 }}>{t('inv.detail.customerCreditOffset')}: {fmt(inv.allocatedCreditAmount)}</div>}
          {!isCredit && customerRefundable > 0.005 && <InvoiceCustomerRefundBalance inv={inv} amount={customerRefundable} />}
          {!isCredit && (inv.customerOverpaymentAllocations || []).length > 0 && <div className="col gap-6 mt-10" style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <div className="t-xs muted" style={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('inv.overpayment.allocations')}</div>
            {(inv.customerOverpaymentAllocations || []).map((allocation) => {
              const incoming = allocation.invoiceId === inv.id;
              return <div key={allocation.id} className="row between gap-8 t-sm" style={{ alignItems: 'center' }}>
                <span>{t(incoming ? 'inv.overpayment.receivedFrom' : 'inv.overpayment.allocatedTo', { document: incoming ? allocation.sourceInvoiceNumber : allocation.invoiceNumber })}</span>
                <span className="row gap-8"><b>{fmt(allocation.amount)}</b><button type="button" className="fak-pay-edit" onClick={() => runAction('reverse-overpayment-allocation', () => window.PlummeBackend.reverseCustomerOverpaymentAllocation({ invoiceId: allocation.invoiceId, settlementId: allocation.id }), t('inv.overpayment.reverseAllocationFailed'))} title={t('inv.overpayment.reverseAllocation')} aria-label={t('inv.overpayment.reverseAllocation')}><Icon name="refresh" size={13}/></button></span>
              </div>;
            })}
          </div>}
          {!isCredit && (inv.relatedCreditNotes || []).some((credit) => credit.refundedAmount > 0) && <div className="t-xs muted mt-8" style={{ fontWeight: 600 }}>{t('inv.detail.relatedRefund')}: {fmt(-(inv.relatedCreditNotes || []).reduce((sum, credit) => sum + credit.refundedAmount, 0))}</div>}
          {/* Er det kreditert mer enn fakturaen har igjen, er «Gjenstaar 0» bare halve
              sannheten: resten er penger kunden har til gode. Det skal staa her, ikke
              bare dukke opp som forhaandsutfylt belop i tilbakebetalingsdialogen. */}
          {displayedPayments.length > 0 && <div className="col gap-6 mt-10" style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <div className="t-xs muted" style={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }}>{t(isCredit ? 'inv.detail.registeredRefunds' : 'inv.detail.registeredPayments')}</div>
            {displayedPayments.map((p) => <div key={p.id} className="fak-pay-row row between t-sm" style={{ fontWeight: 700, alignItems: 'center', width: '100%', padding: '4px 0', textAlign: 'left' }}>
              <span className="row gap-8" style={{ alignItems: 'center', minWidth: 0 }}><Icon name="check" size={13} style={{ color: 'var(--success)' }} /><span className="muted" style={{ fontWeight: 600 }}>{p.date}</span><span className="t-xs muted" style={{ fontWeight: 600 }}>· {p.direction === 'outgoing' && !isCredit ? t('inv.overpayment.refund') + ' · ' : ''}{paymentAccountLabel(p.account)}{Number(p.feeAmount)>0?` · ${t('inv.detail.paymentFeeBank', { fee: fmt(p.feeAmount), bank: fmt(p.bankAmount) })}`:''}</span></span>
              <span className="row gap-8" style={{ alignItems: 'center' }}><b className="t-num">{fmt((isCredit || p.direction === 'outgoing') ? -p.amount : p.amount)}</b><button type="button" className="fak-pay-edit" onClick={() => openPaymentDialog(p)} title={t(p.direction === 'outgoing' ? 'inv.pay.editRefundTitle' : 'inv.pay.editPaymentTitle')} aria-label={t(p.direction === 'outgoing' ? 'inv.pay.editRefundTitle' : 'inv.pay.editPaymentTitle')}><Icon name="pencil" size={13} /></button></span>
            </div>)}
          </div>}
          <div className="row wrap gap-10 mt-12">
            {canPay && (rest > 0 || (!isCredit && paid)) && <Button size="sm" icon="plus" variant="secondary" style={rest > 0 ? undefined : {width:'100%'}} onClick={() => openPaymentDialog()}>{t(isCredit ? 'inv.detail.registerRefund' : 'inv.detail.registerPayment')}</Button>}
            {!isCredit && rest > 0 && <Button size="sm" icon="link" variant="secondary" onClick={() => setLinkPostingOpen(true)} data-help={t('helpTopics.settlement.body')} data-help-title={t('helpTopics.settlement.title')}>{t('settlement.linkExisting')}</Button>}
          </div>
          {!isCredit && rest > 0 && (inv.availableCreditNotes || []).length > 0 && <Button size="sm" icon="swap" variant="secondary" style={{ marginTop: 12, marginLeft: 8 }} onClick={offsetCustomerCredit} data-help={t('helpTopics.overpayment.body')} data-help-title={t('helpTopics.overpayment.title')}>{t('inv.detail.offsetCustomerCredit')}</Button>}
          {!isCredit && rest > 0 && (inv.availableCustomerOverpayments || []).length > 0 && <Button size="sm" icon="swap" variant="secondary" style={{ marginTop: 12, marginLeft: 8 }} onClick={openOverpaymentAllocation} data-help={t('helpTopics.overpayment.body')} data-help-title={t('helpTopics.overpayment.title')}>{t('inv.overpayment.allocate')}</Button>}
          {!isCredit && customerRefundable > 0.005 && <Button size="sm" icon="swap" variant="secondary" style={{ marginTop: 12, width: '100%' }} onClick={openOverpaymentRefund}>{t('inv.detail.registerRefund')}</Button>}
        </React.Fragment>}
      </div>
    </div>
    {/* En e-post som kom i retur skal si fra der brukeren ser etter, ikke bare
        ligge som et webhook-event. «Sendt» alene er ikke godt nok svar. */}
    {delivery.failed && <div className="fak-box mt-14" style={{ borderColor: 'var(--error)', background: 'var(--error-bg)' }}>
      <div className="row gap-10" style={{ alignItems: 'flex-start' }}>
        <Icon name="alertTri" size={18} style={{ color: 'var(--error)', flex: 'none', marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <b className="t-sm" style={{ color: 'var(--error)' }}>{delivery.label}{delivery.to ? ` — ${delivery.to}` : ''}</b>
          <div className="t-sm muted" style={{ fontWeight: 600, marginTop: 3 }}>
            {delivery.message ? `${delivery.message}. ` : ''}{t('inv.detail.deliveryFailureHelp')}
          </div>
        </div>
      </div>
    </div>}
    <div className="fak-box mt-14" data-help-title={t('inv.detail.amountTitle')} data-help={t('inv.detail.amountHelp')}>
      <div className="t-h3" style={{ fontSize: 14, marginBottom: 8 }}>{t('inv.detail.amountTitle')}</div>
      <div className="kv">
        <div className="kv-row"><span className="kv-key">{t('inv.detail.net')}</span><span className="kv-val">{fmt(inv.net)}</span></div>
        <div className="kv-row"><span className="kv-key">{t('ui.summary.vat')}</span><span className="row gap-16"><span className="t-sm muted" style={{ fontWeight: 700 }}>{mvaPct} %</span><span className="kv-val">{fmt(mva)}</span></span></div>
        <div className="kv-row"><span className="kv-key" style={{ fontWeight: 900 }}>{t('inv.detail.totalAmount')}</span><span className="kv-val" style={{ fontWeight: 900 }}>{fmt(inv.amount)}</span></div>
      </div>
    </div>
    <div className="fak-box mt-14" data-help-title={t('inv.kpi.history')} data-help={t('inv.detail.timelineHelp')}>
      <div className="t-h3" style={{ fontSize: 14, marginBottom: 10 }}>{t('inv.detail.history')}</div>
      <div className="col gap-10">{history.map(([eventLabel, date, tone], i) => <div key={i} className="row gap-10" style={{ alignItems: 'center' }}>
        <span className="fak-dot" style={{ background: tone === 'error' ? 'var(--error)' : tone === 'ok' ? 'var(--success)' : 'var(--muted-2)' }} />
        <span className="grow t-sm" style={{ fontWeight: 700, color: tone === 'error' ? 'var(--error)' : undefined }}>{eventLabel}</span><span className="t-xs muted">{date}</span></div>)}</div>
    </div>
    <div className="row gap-10 mt-16 wrap fak-acts-inline">{acts.map((a, i) => <Button key={i} size="sm" variant={a.variant} icon={a.icon} onClick={a.onClick} disabled={Boolean(busyAction)} {...(a.help ? { 'data-help': a.help, 'data-help-title': a.label } : {})}>{a.label}</Button>)}</div>
    <div className="fak-acts-menu mt-16 popwrap">
      <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setMenu((o) => !o)}><Icon name="sliders" size={16} />{t('inv.detail.action')}<Icon name={menu ? 'chevDown' : 'chevUp'} size={15} /></button>
      {menu && <React.Fragment><div className="pop-scrim" onClick={() => setMenu(false)} /><div className="pop pop-menu" style={{ bottom: 'calc(100% + 8px)', top: 'auto', left: 0, right: 0 }}>{acts.map((a, i) => <button key={i} disabled={Boolean(busyAction)} onClick={() => { void a.onClick(); setMenu(false); }} style={a.variant === 'danger' ? { color: 'var(--error)' } : null}><Icon name={a.icon} size={18} />{a.label}</button>)}</div></React.Fragment>}
    </div>
    {(dlg === 'issue' || dlg === 'issueOnly') && <InvoiceBulkDialog action={dlg} rows={[inv]} busy={Boolean(busyAction)} onClose={() => { if (!busyAction) setDlg(null); }} onConfirm={(action, options) => runAction(action, async () => { const summary = await invoiceListRunBulkAction(action, [inv], options); toast(summary.message, summary.tone); setDlg(null); })} />}
    {pdfPreview && <Modal open portal onClose={() => setPdfPreview(null)} title={(pdfPreview.title || pdfPreview.name || inv.num || t('inv.type.unknown'))} max={980}
      footer={<Button variant="secondary" onClick={() => setPdfPreview(null)}>{t('common.close')}</Button>}>
      {pdfPreview.loading ? <div className="pdfv-msg"><b>{t('inv.detail.loadingDoc')}</b></div> :
        pdfPreview.error ? <div className="pdfv-msg"><b style={{ color: 'var(--error)' }}>{pdfPreview.error}</b></div> :
        <PdfViewer src={pdfPreview.src} name={pdfPreview.name || (inv.num + '.pdf')} />}
    </Modal>}
    {/* Beløpet skrives inn positivt også på en kreditnota — fortegnet hører hjemme i
        visningen og bokføringen, ikke i tastingen. */}
    {dlg === 'pay' && <Modal open portal onClose={() => { setDlg(null); setPaymentEditor(null); setPaymentTargetId(''); }}
      title={t(paymentIsRefund ? (paymentEditor ? 'inv.pay.editRefundTitle' : 'inv.pay.registerRefundTitle') : (paymentEditor ? 'inv.pay.editPaymentTitle' : 'inv.pay.registerPaymentTitle'))} max={760}
      footer={<React.Fragment>{paymentEditor && <InvoicePaymentUndoButton payment={paymentEditor} isRefund={paymentIsRefund} disabled={Boolean(busyAction)} onReversed={() => { setDlg(null); setPaymentEditor(null); setPaymentTargetId(''); }} />}<div style={{ flex: 1 }} /><Button variant="ghost" onClick={() => { setDlg(null); setPaymentEditor(null); setPaymentTargetId(''); }}>{t('common.cancel')}</Button><Button icon="check" disabled={Boolean(busyAction)||!payForm.date||!settlementPreview.ready} onClick={savePayment}>{t(paymentEditor ? 'inv.pay.saveChange' : paymentIsRefund ? 'inv.detail.registerRefund' : 'inv.detail.registerPayment')}</Button></React.Fragment>}>
      <div className="fak-pay-modal-grid">
        <Field label={t(paymentIsRefund ? 'inv.pay.paidFrom' : 'sale.pay.paidTo')}><InvoicePayAccountSelect value={payForm.settlementAccountCode} direction="inbound" onChange={(value) => setPayForm((current) => ({ ...current, settlementAccountCode: value }))} /></Field>
        <Field label={t(paymentIsRefund ? 'inv.pay.refundDate' : 'inv.pay.paidDate')}><Input type="date" value={payForm.date} onChange={(e) => setPayForm((current) => ({ ...current, date: e.target.value }))} /></Field>
        {foreignPayment && !paymentIsRefund
          ? <React.Fragment><Field label={t('inv.pay.originalAmount', 'Beløp i {currency}', { currency: inv.currency })}><Currency suffix={inv.currency} value={payForm.originalAmount} onChange={(e) => setPayForm((current) => ({ ...current, originalAmount: e.target.value }))} /></Field><Field label={t('inv.pay.bankAmountCurrency', 'Bankbeløp i {currency}', { currency: inv.accountingCurrency })}><Currency suffix={inv.accountingCurrency} value={payForm.bankAmount} onChange={(e) => setPayForm((current) => ({ ...current, bankAmount: e.target.value }))} /></Field></React.Fragment>
          : <Field label={t(paymentIsRefund ? 'inv.pay.refundAmount' : 'inv.detail.amountTitle')}><Currency value={payForm.amount} onChange={(e) => setPayForm((current) => ({ ...current, amount: e.target.value }))} /></Field>}
        <Field label={t('inv.pay.fee')} help={t('helpTopics.fee.body')} helpTitle={t('helpTopics.fee.title')}><Currency value={payForm.feeAmount} onChange={(e) => setPayForm((current) => ({ ...current, feeAmount: e.target.value }))} /></Field>
        <Field label={t('inv.pay.feeAccount')}><Input value={payForm.feeAccountCode} onChange={(e) => setPayForm((current) => ({ ...current, feeAccountCode: e.target.value }))} /></Field>
      </div>
      {invoiceListNumber(payForm.feeAmount) > 0 && <p className="fak-pay-modal-note">{t(paymentIsRefund ? 'inv.pay.bankAmountOut' : 'inv.pay.bankAmountIn', { amount: fmt(Math.max(0, invoiceListNumber(payForm.amount) + (paymentIsRefund ? 1 : -1) * invoiceListNumber(payForm.feeAmount))) })}</p>}
      {settlementPreview.error&&<p role="alert">{settlementPreview.error}</p>}
      {foreignPayment && !paymentIsRefund && <p className="fak-pay-modal-note">{t('inv.pay.fxPreview', 'Fordringen lukkes med {receivable} {accountingCurrency}. Differansen mot bankbeløpet føres som {kind}: {difference}.', { receivable: fmt(settlementCarrying), accountingCurrency: inv.accountingCurrency, kind: invoiceListNumber(payForm.bankAmount) >= settlementCarrying ? t('inv.pay.fxGain', 'valutagevinst') : t('inv.pay.fxLoss', 'valutatap'), difference: fmt(Math.abs(invoiceListNumber(payForm.bankAmount) - settlementCarrying)) })}</p>}
      {paymentIsRefund && <p className="fak-pay-modal-note">{t('inv.pay.refundHelp')}</p>}
      {(paymentTargetId ? relatedRefundTarget?.remainingAmount : rest) > 0 && <p className="fak-pay-modal-note">{t(paymentIsRefund ? 'inv.pay.refundNote' : 'inv.pay.remainingNote', { amount: fmt(paymentTargetId ? relatedRefundTarget?.remainingAmount : rest) })}</p>}
      {!foreignPayment && !paymentIsRefund && invoiceListNumber(payForm.amount) > rest + 0.005 && <div className="bg-note info mt-12"><Icon name="info" size={18}/><span>{t('inv.pay.overpaymentNote', { amount: fmt(invoiceListNumber(payForm.amount) - rest) })}</span></div>}
    </Modal>}
    {dlg === 'overpayment-refund' && <InvoiceCustomerRefundDialog amount={customerRefundable} form={overpaymentForm} setForm={setOverpaymentForm} busy={Boolean(busyAction)} onClose={() => setDlg(null)} onSave={saveOverpaymentRefund} />}
    {dlg === 'overpayment-allocation' && <Modal open portal onClose={() => setDlg(null)} title={t('inv.overpayment.allocate')} max={560}
      footer={<React.Fragment><Button variant="ghost" onClick={() => setDlg(null)}>{t('common.cancel')}</Button><Button icon="check" disabled={Boolean(busyAction)} onClick={saveOverpaymentAllocation} data-help={t('helpTopics.overpayment.body')} data-help-title={t('helpTopics.overpayment.title')}>{t('inv.overpayment.allocate')}</Button></React.Fragment>}>
      <div className="col gap-12">
        <Field label={t('inv.overpayment.source')}><Select value={overpaymentForm.sourceInvoiceId} onChange={(e) => { const source = (inv.availableCustomerOverpayments || []).find((entry) => entry.id === e.target.value); setOverpaymentForm((current) => ({ ...current, sourceInvoiceId: e.target.value, amount: String(Math.min(rest, Number(source?.amount) || 0)) })); }}>{(inv.availableCustomerOverpayments || []).map((entry) => <option key={entry.id} value={entry.id}>{entry.num} · {fmt(entry.amount)}</option>)}</Select></Field>
        <Field label={t('inv.detail.amountTitle')}><Currency value={overpaymentForm.amount} onChange={(e) => setOverpaymentForm((current) => ({ ...current, amount: e.target.value }))}/></Field>
        <Field label={t('inv.pay.paidDate')}><Input type="date" value={overpaymentForm.date} onChange={(e) => setOverpaymentForm((current) => ({ ...current, date: e.target.value }))}/></Field>
      </div>
    </Modal>}
    {linkPostingOpen && <ManualPostingSettlementModal target={{ documentType: 'invoice', documentId: inv.id }} onClose={() => setLinkPostingOpen(false)}/>}
    {dlg === 'packing' && <PakkseddelDialog inv={inv} onClose={() => setDlg(null)} onPreview={previewPackingSlip} onDownload={downloadPackingSlip} busy={busyAction} />}
    {dlg === 'credit' && <CreditDialog inv={inv} initialSelections={assistantCreditSelections} onClose={() => {setDlg(null);setAssistantCreditSelections(null);}} busy={Boolean(busyAction)} onCreate={({ selections, partial, date }) => runAction('credit', async () => {
      if (!window.PlummeBackend?.creditInvoice) throw new Error(t('inv.err.creditNotConnected'));
      const created = await window.PlummeBackend.creditInvoice(inv, { selections, date });
      setDlg(null);
      // Kreditnotaen lages som utkast. Brukeren skal rett inn i den — aarsak og
      // referanse mangler fortsatt, og aa lete den opp under «Utkast» etterpaa var
      // et ekstra steg uten hensikt.
      const draft = invoiceListOpenDraftPayload(created);
      if (draft) {
        window.PlummeBackend?.setOpenInvoice?.(draft);
        toast(t(partial ? 'inv.toast.partialCreditOpened' : 'inv.toast.creditOpened', { document: inv.num }), 'success');
        setRoute && setRoute('faktura');
        return;
      }
      toast(t('inv.toast.creditCreated', { document: t('inv.type.creditNote'), number: inv.num }), 'success');
    }, t('inv.err.creditFailed'))} />}
    {/* Modaltittelen er brukervendt og skal følge UI-språket, ikke dokumentets språk. */}
    {dlg === 'send' && <Modal open portal onClose={() => setDlg(null)} title={t('inv.send.title', { document: documentLabelCap(inv), number: inv.num || '' }).trim()} max={520}
      footer={<React.Fragment><Button variant="ghost" onClick={() => setDlg(null)}>{t('common.cancel')}</Button><Button icon="send" disabled={Boolean(busyAction) || !String(sendForm.email || '').trim()} onClick={sendDocument}>{sendActionLabel(inv)}</Button></React.Fragment>}>
      <div className="col gap-14" style={{ paddingBottom: 4 }}>
        <div className="row gap-12" style={{ alignItems: 'flex-start' }}><p className="t-body" style={{ margin: 0 }}>{t('inv.send.body', { document: documentLabelCap(inv), amount: fmt(inv.amount), customer: inv.customer })}{inv.sentAt || inv.status === 'sendt' ? t('inv.send.resendNote') : ''}</p></div>
        <Field label={t('sale.send.how')} hint={t(sendForm.deliveryMode === 'link' ? 'sale.send.linkHint' : 'sale.send.pdfHint')}>
          <Segmented block options={[
            { value: 'attachment', label: t('sale.send.asPdf'), icon: 'fileText' },
            { value: 'link', label: t('sale.send.asLink'), icon: 'link' },
          ]} value={sendForm.deliveryMode} onChange={(value) => setSendForm((current) => ({ ...current, deliveryMode: value }))} />
        </Field>
        <InvoiceRecipientField document={inv._backend || inv} customerName={inv.customer} value={sendForm}
          onChange={(next) => setSendForm((current) => ({ ...current, ...next }))} />
        <Field label={t('sub.subject')}><Input value={sendForm.subject} onChange={(event) => setSendForm((current) => ({ ...current, subject: event.target.value }))} /></Field>
      </div>
    </Modal>}
    {dlg === 'deliver' && <Modal open portal onClose={() => setDlg(null)} title={t('inv.deliver.title')} max={480}
      footer={<React.Fragment><Button variant="ghost" onClick={() => setDlg(null)}>{t('common.cancel')}</Button><Button icon="check" disabled={Boolean(busyAction)} onClick={() => runAction('deliver', async () => {
        if (!window.PlummeBackend?.markInvoiceDeliveredExternally) throw new Error(t('inv.err.deliverNotConnected'));
        await window.PlummeBackend.markInvoiceDeliveredExternally(inv);
        setDlg(null);
        toast(t('inv.toast.deliveredExternally'), 'success');
      }, t('inv.err.deliverFailed'))}>{t('inv.act.markDelivered')}</Button></React.Fragment>}>
      <p className="t-body" style={{ margin: 0 }}>{t('inv.deliver.body')}</p>
    </Modal>}
    {dlg === 'delete' && <Modal open portal onClose={() => setDlg(null)} title={t('sale.deleteDraftTitle')} max={440}
      footer={<React.Fragment><Button variant="ghost" onClick={() => setDlg(null)}>{t('common.cancel')}</Button><Button variant="danger-solid" icon="trash" disabled={Boolean(busyAction)} onClick={() => runAction('delete', async () => { if (!window.PlummeBackend?.deleteInvoice) throw new Error(t('inv.err.deleteNotConnected')); const deleted = await window.PlummeBackend.deleteInvoice(inv); if (deleted === false) { toast(t('inv.toast.onlyDrafts'), 'warning'); return; } toast(t('inv.toast.draftDeleted'), 'success'); setDlg(null); }, t('inv.err.deleteFailed'))}>{t('inv.del.confirm')}</Button></React.Fragment>}>
      <div className="row gap-14" style={{ paddingBottom: 8 }}><p className="t-body">{splitAround('inv.del.body', 'customer', <b>{inv.customer}</b>)}</p></div>
    </Modal>}
    {/* Tilbudet er allerede gjort om for. Da skal det si fra, ikke lage nok et
        dokument i stillhet — samme jobb fakturert to ganger er ekte penger. */}
    {dlg === 'convert' && <Modal open portal center onClose={() => setDlg(null)} title={t('inv.convert.title', 'Opprett fra tilbudet')} max={460}
      footer={<Button variant="ghost" onClick={() => setDlg(null)}>{t('common.cancel')}</Button>}>
      <div className="col gap-10" style={{ paddingBottom: 6 }}>
        {/* Er tilbudet gjort om for, skal det sta her — ikke i en egen dialog etterpa.
            Samme jobb fakturert to ganger er ekte penger. */}
        {alreadyConverted && <div style={{ background: 'var(--plum-50)', border: '1px solid var(--plum-200)', borderRadius: 'var(--r-md)', padding: '12px 14px' }}>
          <b className="t-sm">{t('inv.convert.alreadyTitle')}</b>
          {(inv.convertedDocuments || []).map((document) => <div key={document.id} className="row gap-8 mt-8" style={{ alignItems: 'center' }}>
            <Icon name={document.isDraft ? 'pencil' : 'checkCircle'} size={15} className="muted" />
            <span className="t-sm" style={{ fontWeight: 700 }}>{document.isDraft ? `${t('inv.status.draft')} (${invoiceListTypeLabel(document.type)})` : `${invoiceListTypeLabel(document.type)} ${document.num}`}</span>
          </div>)}
          <div className="t-xs muted mt-8" style={{ fontWeight: 600 }}>{t('inv.convert.duplicateWarning')}</div>
        </div>}
        <DocumentActionChoice icon="fileText" label={t('inv.convert.invoice', 'Faktura')} busy={Boolean(busyAction)}
          help={t('inv.convert.invoiceHelp', 'Vanlig betalingsfrist. Bokføres når du oppretter den.')}
          onClick={() => { setDlg(null); createFromOffer('faktura'); }} />
        <DocumentActionChoice icon="box" label={t('inv.convert.order', 'Ordrebekreftelse')} busy={Boolean(busyAction)}
          help={t('inv.convert.orderHelp', 'Bekrefter avtalen uten å fakturere. Kan faktureres senere.')}
          onClick={() => { setDlg(null); createFromOffer('ordre'); }} />
        <p className="t-xs muted" style={{ fontWeight: 600 }}>{t('inv.convert.footerHelp')}</p>
      </div>
    </Modal>}
    {/* «Del» samler handlingene som bare skal faa dokumentet ut av appen. */}
    {dlg === 'share' && <Modal open portal center onClose={() => setDlg(null)} title={t('inv.share.title', 'Del dokumentet')} max={460}
      footer={<Button variant="ghost" onClick={() => setDlg(null)}>{t('common.cancel')}</Button>}>
      <div className="col gap-10" style={{ paddingBottom: 6 }}>
        <DocumentActionChoice icon="link" label={t('inv.act.copyLink')} help={t('inv.act.copyLinkHelp')} busy={Boolean(busyAction)}
          onClick={() => { setDlg(null); copyPublicLink(); }} />
        <DocumentActionChoice icon="download" label={t('inv.act.download')} help={t('inv.act.downloadHelp')} busy={Boolean(busyAction)}
          onClick={() => { setDlg(null); downloadDocument(); }} />
        {inv.type === 'faktura' && <DocumentActionChoice icon="box" label={t('inv.act.packingSlip')} help={t('inv.act.packingSlipHelp')} busy={Boolean(busyAction)}
          onClick={() => setDlg('packing')} />}
      </div>
    </Modal>}
  </div>;
}

// Valgrad i «Opprett»- og «Del»-dialogene: ikon, hva valget gjor, og hva det
// betyr. Teksten under etiketten er grunnen til at handlingene taaler aa ligge
// bak en knapp — de er bedre forklart her enn de var som knapper i raden.
function DocumentActionChoice({ icon, label, help, busy, onClick }) {
  return <button type="button" className="row between" disabled={Boolean(busy)}
    style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-ms)', background: 'var(--card)', textAlign: 'left' }}
    onClick={onClick}>
    <span className="row gap-12" style={{ minWidth: 0 }}>
      <span className="icon-chip" style={{ width: 30, height: 30, borderRadius: 'var(--r-sm)', flex: 'none' }}><Icon name={icon} size={16} /></span>
      <span style={{ minWidth: 0 }}><b>{label}</b>{help && <span className="t-xs muted" style={{ display: 'block', fontWeight: 600 }}>{help}</span>}</span>
    </span>
    <Icon name="chevRight" size={16} className="muted" />
  </button>;
}

// Fanerad for dokumenttyper. Måler den faktiske bredden på fanene og folder de som
// ikke får plass inn i en "Mer"-meny, i stedet for å la raden brekke eller flyte utenfor.
function FakturaTabs({ tab, onSelect, draftCount, help, helpTitle }) {
  const wrapRef = useRef(null);
  const measRef = useRef(null);
  const [vis, setVis] = useState(99);
  const [moreOpen, setMoreOpen] = useState(false);
  const [tilbudOpen, setTilbudOpen] = useState(false);
  const isTilbud = tab === 'tilbud' || tab === 'ordre';
  const TABS = [
    { key: 'faktura', icon: 'fileText', label: t('inv.type.invoice'), active: tab === 'faktura' },
    { key: 'tilbud', icon: tab === 'ordre' ? 'sendCheck' : 'doc', label: t(tab === 'ordre' ? 'inv.type.orderConfirmation' : 'inv.type.quote'), active: isTilbud, group: true },
    { key: 'kreditnota', icon: 'files', label: t('inv.type.creditNote'), active: tab === 'kreditnota' },
    { key: 'utkast', icon: 'save', label: t('inv.tab.drafts'), active: tab === 'utkast', badge: draftCount },
    { key: 'salgsrapport', icon: 'trendUp', label: t('inv.tab.salesReport'), active: tab === 'salgsrapport' },
    { key: 'importhistorikk', icon: 'archive', label: t('inv.importHistory.tab'), active: tab === 'importhistorikk' },
  ];
  const measure = () => {
    const wrap = wrapRef.current, meas = measRef.current;
    if (!wrap || !meas) return;
    const avail = wrap.clientWidth;
    const btns = Array.from(meas.querySelectorAll('[data-tw]'));
    const moreEl = meas.querySelector('[data-tw-more]');
    const gap = 4, pad = 10;
    const widths = btns.map((b) => b.offsetWidth);
    const moreW = moreEl ? moreEl.offsetWidth : 0;
    const sumAll = pad + widths.reduce((a, w) => a + w + gap, 0);
    if (sumAll - gap + pad <= avail) { setVis(TABS.length); return; }
    let used = pad + moreW + gap;
    let n = 0;
    for (let i = 0; i < widths.length; i++) {
      const w = widths[i] + gap;
      if (used + w <= avail) { used += w; n++; } else break;
    }
    setVis(Math.max(1, n));
  };
  React.useLayoutEffect(() => { measure(); }, [tab, draftCount]);
  useEffect(() => {
    measure();
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') { window.addEventListener('resize', measure); return () => window.removeEventListener('resize', measure); }
    const ro = new ResizeObserver(measure); ro.observe(wrap); return () => ro.disconnect();
  }, []);
  const visible = TABS.slice(0, vis);
  const hidden = TABS.slice(vis);
  const pick = (k) => { onSelect(k); setMoreOpen(false); setTilbudOpen(false); };
  const badgeEl = (n) => n > 0 ? <span className="seg-badge">{n}</span> : null;
  // NB: iterasjonsvariablene kan ikke hete `t` her — de ville skygge for den globale t().
  const renderBtn = (item) => item.group
    ? <div className="popwrap" style={{ display: 'inline-flex' }} key="tilbud">
        <button className={item.active ? 'active' : ''} onClick={() => setTilbudOpen((o) => !o)}><Icon name={item.icon} size={16} />{item.label}<Icon name="chevDown" size={14} /></button>
        {tilbudOpen && <React.Fragment><div className="pop-scrim" onClick={() => setTilbudOpen(false)} />
          <div className="pop pop-menu" style={{ minWidth: 200 }}>
            <button onClick={() => pick('tilbud')}><Icon name="doc" size={16} />{t('inv.type.quote')}{tab === 'tilbud' && <Icon name="check" size={16} style={{ marginLeft: 'auto' }} />}</button>
            <button onClick={() => pick('ordre')}><Icon name="sendCheck" size={16} />{t('inv.type.orderConfirmation')}{tab === 'ordre' && <Icon name="check" size={16} style={{ marginLeft: 'auto' }} />}</button>
          </div></React.Fragment>}
      </div>
    : <button key={item.key} className={item.active ? 'active' : ''} onClick={() => pick(item.key)}><Icon name={item.icon} size={16} />{item.label}{badgeEl(item.badge)}</button>;
  const hasActiveHidden = hidden.some((item) => item.active);
  return <div className="fak-tabs" ref={wrapRef} {...(help ? { 'data-help': help, 'data-help-title': helpTitle } : {})}>
    <div className="seg fak-seg">
      {visible.map(renderBtn)}
      {hidden.length > 0 && <div className="popwrap" style={{ display: 'inline-flex' }}>
        <button className={'fak-more' + (hasActiveHidden ? ' active' : '') + (moreOpen ? ' open' : '')} onClick={() => setMoreOpen((o) => !o)}>{t('inv.more')}<Icon name="chevDown" size={14} /></button>
        {moreOpen && <React.Fragment><div className="pop-scrim" onClick={() => setMoreOpen(false)} />
          <div className="pop pop-menu right" style={{ minWidth: 210 }}>
            {hidden.map((item) => item.group
              ? <React.Fragment key="tilbud-m">
                  <button onClick={() => pick('tilbud')}><Icon name="doc" size={16} />{t('inv.type.quote')}{tab === 'tilbud' && <Icon name="check" size={16} style={{ marginLeft: 'auto' }} />}</button>
                  <button onClick={() => pick('ordre')}><Icon name="sendCheck" size={16} />{t('inv.type.orderConfirmation')}{tab === 'ordre' && <Icon name="check" size={16} style={{ marginLeft: 'auto' }} />}</button>
                </React.Fragment>
              : <button key={item.key} onClick={() => pick(item.key)}><Icon name={item.icon} size={16} />{item.label}{badgeEl(item.badge)}{item.active && <Icon name="check" size={16} style={{ marginLeft: 'auto' }} />}</button>)}
          </div></React.Fragment>}
      </div>}
    </div>
    <div className="seg fak-seg fak-seg-meas" ref={measRef} aria-hidden="true">
      {TABS.map((item) => item.group
        ? <button key="m-tilbud" data-tw><Icon name={item.icon} size={16} />{item.label}<Icon name="chevDown" size={14} /></button>
        : <button key={'m-' + item.key} data-tw><Icon name={item.icon} size={16} />{item.label}{badgeEl(item.badge)}</button>)}
      <button data-tw-more className="fak-more">{t('inv.more')}<Icon name="chevDown" size={14} /></button>
    </div>
  </div>;
}

function FakturalistePage({ setRoute, year }) {
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const [initialRouteIntent] = useState(consumeInvoiceListRouteIntent);
  const [showBilagReturn] = useState(consumeInvoiceListBilagReturnFlag);
  const [tab, setTab] = useState(() => INVOICE_LIST_TABS_WITH_REPORT.includes(initialRouteIntent?.tab) ? initialRouteIntent.tab : 'faktura');
  const [filter, setFilter] = useState(() => makeInvoiceListFilter(initialRouteIntent));
  const [search, setSearch] = useState('');
  const [exp, setExp] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkBusy, setBulkBusy] = useState('');
  const [lastSelectedId, setLastSelectedId] = useState('');
  const [highlight, setHighlight] = window.PlummeGlobalSearch.useTarget('__highlightInvoice');
  const [importedInvoiceRows, setImportedInvoiceRows] = useState([]);
  const [importedStatusRefresh, setImportedStatusRefresh] = useState(0);
  const openedHighlightRef = useRef(null);
  // Radmodus (S4, invoice-list-rows.jsx): med radmarkør leses sidene fra radene, og arraymodellen bygges ikke.
  const rowsMode = Boolean(window.PlummeInvoiceRowList?.invoiceRowListAvailable?.());
  const model = rowsMode ? window.PlummeInvoiceRowList.getInvoiceRowListModel({ year }) : window.PlummeBackend?.getInvoiceListModel ? window.PlummeBackend.getInvoiceListModel({ year }) : { rows: [], paymentAccounts: [] };
  const activeListYear = Number(year || model.year);

  useEffect(() => {
    const requested = consumeInvoiceListRouteIntent();
    if (!requested || !INVOICE_LIST_TABS_WITH_REPORT.includes(requested.tab)) return;
    setTab(requested.tab);
    setFilter(makeInvoiceListFilter(requested));
    setExp(null);
  }, [backend?.appState, backend?.activeBusinessId]);

  useEffect(() => {
    if (!highlight) return undefined;
    // NB: kan ikke hete `t` — ville skygge for den globale t().
    const timer = setTimeout(() => setHighlight(null), 2600);
    return () => clearTimeout(timer);
  }, [highlight]);

  useEffect(() => {
    let active = true;
    const summary = backend?.appState?.accountingImportSalesHistorySummary;
    const hasImportForYear = Boolean(summary?.years?.[String(activeListYear)] && window.PlummeBackend?.getAccountingImportSalesHistory);
    if (!hasImportForYear) {
      setImportedInvoiceRows([]);
      return undefined;
    }
    (async () => {
      try {
        const pageSize = 1000;
        let page = 1;
        let pages = 1;
        const rows = [];
        while (page <= pages) {
          const result = await window.PlummeBackend.getAccountingImportSalesHistory({ year: activeListYear, page, pageSize, compact: true });
          rows.push(...(result?.rows || []));
          pages = Number(result?.pages || 1) || 1;
          page += 1;
        }
        if (active) setImportedInvoiceRows(mapImportedInvoiceHistoryRows(rows));
      } catch (error) {
        if (active) setImportedInvoiceRows([]);
      }
    })();
    return () => { active = false; };
  }, [backend?.appState?.accountingImportSalesHistorySummary, backend?.appState?.accountingImportSalesHistorySettlements, backend?.activeBusinessId, activeListYear, importedStatusRefresh]);

  const STATUS_OPTS = {
    // «Kreditert» er en egen sluttstatus på fakturaen: fullkrediterte fakturaer er
    // hverken betalt eller forfalt, og må kunne filtreres fram som sin egen gruppe.
    // Fanene her viser bare opprettede dokumenter — utkast har sin egen fane, og
    // «Ikke sendt» er derfor status `opprettet`, ikke `utkast`.
    faktura: [{ key: 'opprettet', label: t('inv.status.created') }, { key: 'sendt', label: t('inv.status.sent') }, { key: 'betalt', label: t('inv.status.paid') }, { key: 'ikkebetalt', label: t('inv.status.unpaid') }, { key: 'forfalt', label: t('inv.status.overdue') }, { key: 'kreditert', label: t('inv.status.credited') }],
    tilbud: [{ key: 'opprettet', label: t('inv.status.created') }, { key: 'sendt', label: t('inv.status.sent') }, { key: 'akseptert', label: t('inv.status.accepted') }],
    ordre: [{ key: 'opprettet', label: t('inv.status.created') }, { key: 'sendt', label: t('inv.status.sent') }, { key: 'bekreftet', label: t('inv.status.confirmed') }],
    kreditnota: [{ key: 'kreditert', label: t('inv.status.credited') }],
    utkast: [{ key: 'utkast', label: t('inv.status.draft') }],
  };
  const archiveTab = tab === 'tilbud' || tab === 'ordre';
  const showArchived = archiveTab && Boolean(filter.checks?.__arkiv);
  const validStatusKeys = new Set((STATUS_OPTS[tab] || []).map((option) => option.key));
  const statusKeys = Object.keys(filter.checks || {}).filter((k) => filter.checks[k] && validStatusKeys.has(k));
  const liveRows = model.rows || [];
  const liveDocumentKeys = new Set(liveRows.map((invoice) => `${invoice.type}:${String(invoice.num || '').trim()}`).filter((key) => !key.endsWith(':')));
  const visibleImportedRows = importedInvoiceRows.map((invoice) => liveDocumentKeys.has(`${invoice.type}:${String(invoice.num || '').trim()}`)
    ? { ...invoice, importedNumberCollision: true, importedNote: 'Importert historikk - samme fakturanummer finnes ogsaa som Plumme-faktura. Historikkraden overskriver ikke live faktura.' }
    : invoice);
  const ofYear = [...liveRows, ...visibleImportedRows].filter((i) => invoiceListYear(i) === activeListYear);
  const draftItems = ofYear.filter((i) => i.status === 'utkast');
  const draftTypeKeys = draftTypeOptions().filter((option) => filter.checks?.[option.key]).map((option) => option.type);
  const rowList = useInvoiceRowList({ enabled: rowsMode, year: activeListYear, tab, statusKeys, draftTypeKeys, archiveTab, showArchived, filter, search });
  const list = rowList.active ? rowList.rows : ofYear.filter((i) => tab === 'utkast' ? i.status === 'utkast' && (!draftTypeKeys.length || draftTypeKeys.includes(i.type)) : i.type === tab && i.status !== 'utkast')
    .filter((i) => archiveTab ? (showArchived ? Boolean(i.archivedAt) : !i.archivedAt) : true)
    .filter((i) => !statusKeys.length || statusKeys.some((key) => key === 'ikkebetalt' ? (i.status === 'sendt' || i.status === 'forfalt') : key === i.status))
    .filter((i) => amountInFilter(i.amount, filter))
    .filter((i) => dateInFilter(i.date, filter))
    .filter((i) => window.PlummeListSearch.matches(search, {
      text: [i.num, i.id, i.customer, i._backend?.invoiceNumber, i._backend?.number, i._backend?.documentNumber, i._backend?.recurringOccurrenceId],
      amounts: [i.amount, i.net, i.paidAmount, i.remainingAmount],
    }));
  useInvoiceListControl({ year: activeListYear, list, tab, search, selectedIds, exp, filter, paymentAccounts: model.paymentAccounts, setTab, setSearch, setSelectedIds, setExp, setFilter, setBulkAction });
  const selectableRows = list.filter(invoiceListCanBulkSelect);
  const selectedRows = list.filter((invoice) => selectedIds.has(invoice.id));
  const allSelected = selectableRows.length > 0 && selectableRows.every((invoice) => selectedIds.has(invoice.id));
  const someSelected = selectableRows.some((invoice) => selectedIds.has(invoice.id));
  const setSelectionRange = (invoice, event) => {
    if (!invoiceListCanBulkSelect(invoice)) return;
    const isRange = Boolean(event?.shiftKey && lastSelectedId);
    const isAdditive = Boolean(event?.ctrlKey || event?.metaKey || event?.shiftKey);
    setSelectedIds((current) => {
      const next = new Set(isAdditive ? current : []);
      if (isRange) {
        const start = selectableRows.findIndex((row) => row.id === lastSelectedId);
        const end = selectableRows.findIndex((row) => row.id === invoice.id);
        if (start >= 0 && end >= 0) {
          selectableRows.slice(Math.min(start, end), Math.max(start, end) + 1).forEach((row) => next.add(row.id));
          return next;
        }
      }
      if (next.has(invoice.id)) next.delete(invoice.id); else next.add(invoice.id);
      return next;
    });
    setLastSelectedId(invoice.id);
  };
  const toggleSelection = (invoice, event) => setSelectedIds((current) => {
    const next = new Set(current);
    if (event?.shiftKey && lastSelectedId) {
      const start = selectableRows.findIndex((row) => row.id === lastSelectedId);
      const end = selectableRows.findIndex((row) => row.id === invoice.id);
      if (start >= 0 && end >= 0) selectableRows.slice(Math.min(start, end), Math.max(start, end) + 1).forEach((row) => next.add(row.id));
      else if (next.has(invoice.id)) next.delete(invoice.id); else next.add(invoice.id);
    } else if (next.has(invoice.id)) next.delete(invoice.id); else next.add(invoice.id);
    setLastSelectedId(invoice.id);
    return next;
  });
  const clearSelection = () => { setSelectedIds(new Set()); setLastSelectedId(''); };
  const toggleSelectVisible = () => setSelectedIds((current) => {
    const next = new Set(current);
    if (allSelected) selectableRows.forEach((invoice) => next.delete(invoice.id));
    else selectableRows.forEach((invoice) => next.add(invoice.id));
    return next;
  });
  useEffect(() => {
    const visibleIds = new Set(list.map((invoice) => invoice.id));
    setSelectedIds((current) => {
      const next = new Set([...current].filter((id) => visibleIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [list.map((invoice) => invoice.id).join('|')]);
  // Selve kjoringen ligger i `invoice-list-bulk.jsx`. Siden eier bare utvalget,
  // travelhetsflagget og oppsummeringen brukeren far tilbake.
  const runBulkAction = async (action, options = {}) => {
    setBulkBusy(action);
    try {
      const summary = await invoiceListRunBulkAction(action, selectedRows, options);
      toast(summary.message, summary.tone);
      setBulkAction(null);
      clearSelection();
    } catch (error) {
      toast(error?.message || t('inv.bulk.err.failed'), 'error');
    } finally {
      setBulkBusy('');
    }
  };
  useEffect(() => {
    const requested = highlight || window.__highlightInvoice || null;
    if (!requested) return;
    if (openedHighlightRef.current === requested) return;
    const match = ofYear.find((invoice) => String(invoice.num || '') === String(requested) || String(invoice.id || '') === String(requested));
    // I radmodus finnes ikke hele året i minnet; søket på nummeret henter raden fra serveren.
    if (!match) { if (rowList.active) { openedHighlightRef.current = requested; setFilter(makeFilter()); setSearch(String(requested)); clearSelection(); window.__highlightInvoice = null; } return; }
    openedHighlightRef.current = requested;
    const nextSearch = String(match.num || match.id || requested);
    if (INVOICE_LIST_TABS.includes(match.type) && match.type !== tab) {
      setTab(match.type);
      setFilter(makeFilter());
      setSearch(nextSearch);
      setExp(match.id);
      clearSelection();
      window.__highlightInvoice = null;
      setTimeout(() => document.querySelector('.fak-tbl .row-flash')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 80);
      return;
    }
    setFilter(makeFilter());
    setSearch(nextSearch);
    setExp(match.id);
    clearSelection();
    window.__highlightInvoice = null;
    setTimeout(() => document.querySelector('.fak-tbl .row-flash')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 80);
  }, [backend?.appState, backend?.activeBusinessId, highlight, tab, year]);
  const reportRevenue = Number(window.PlummeBackend?.getReportsModel?.({ year: activeListYear, includeChart: false, includeTrial: false })?.result?.inntekter);
  // Alle dokumenter i aaret der siste sending kom i retur — uavhengig av fane og
  // filter, slik at en avvist faktura ikke kan gjemme seg bak et aktivt sok.
  const failedDeliveries = rowList.active ? (model.failedDeliveries || []) : ofYear.filter((invoice) => invoice.delivery?.failed);
  const importedSalesHistoryNeedsReanalysis = backend?.appState?.accountingImportSalesHistorySummary?.source === 'complete-accounting-export'
    && backend.appState.accountingImportSalesHistorySummary.schemaVersion !== '2026-08-15-credit-notes-v1';
  const openAccountingImportReanalysis = () => {
    setRoute?.('innstillinger');
    setTimeout(() => window.dispatchEvent?.(new CustomEvent('plumme-settings-tab', { detail: 'data' })), 0);
  };
  const tabItems = (tab === 'utkast' ? draftItems : ofYear.filter((i) => i.type === tab).filter((i) => i.status !== 'utkast')).filter((i) => archiveTab ? (showArchived ? Boolean(i.archivedAt) : !i.archivedAt) : true);
  const statusLabel = (status) => t('inv.status.' + status, status);
  const STATUS_SORT_ORDER = { utkast: 10, opprettet: 15, sendt: 20, forfalt: 30, akseptert: 40, bekreftet: 40, betalt: 50, kreditert: 60 };
  const TAB_LABEL = { faktura: 'faktura', tilbud: 'tilbud', ordre: 'ordrebekreftelse', kreditnota: 'kreditnota' };
  const reportExportRows = ofYear.filter((invoice) => invoice.type === 'faktura')
    .filter((invoice) => invoice.status !== 'utkast')
    .filter((invoice) => dateInFilter(invoice.date, filter))
    .filter((invoice) => amountInFilter(invoice.net, filter));
  const exportRows = tab === 'salgsrapport' ? reportExportRows : list;
  const exportData = {
    filename: 'fakturaliste-' + (TAB_LABEL[tab] || tab) + '-' + year + (rowList.windowed ? t('inv.rows.pageFileSuffix') : ''),
    columns: [
      { header: t('inv.col.number'), value: (i) => i.num },
      { header: t('inv.col.customer'), value: (i) => i.customer },
      { header: t('inv.col.date'), value: (i) => i.date },
      { header: t('inv.col.net'), value: (i) => i.net },
      { header: t('inv.col.amount'), value: (i) => i.amount },
      { header: t('inv.col.status'), value: (i) => statusLabel(i.status) },
    ],
    rows: exportRows,
  };
  // Tallene over lista: aggregater i radmodus, arrayene ellers (invoice-list-rows.jsx).
  const stats = rowList.active ? rowList.stats : invoiceListArrayStats({ ofYear, tabItems, draftItems });
  const tabKpis = invoiceListKpis(tab, stats, Number.isFinite(reportRevenue) ? reportRevenue : stats.documentRevenue);

  const filterEl = <FilterPanel value={filter} onChange={setFilter} onReset={() => setFilter(makeFilter())} dates period amount year={year} help={t('inv.filterHelp')} groups={tab === 'salgsrapport' ? undefined : tab === 'utkast' ? [{ label: t('inv.filterDocType'), options: draftTypeOptions() }] : [{ label: t('inv.col.status'), options: STATUS_OPTS[tab] }, ...(archiveTab ? [{ label: t('inv.filterArchive'), options: [{ key: '__arkiv', label: t('inv.filterSeeArchived') }] }] : [])]} />;
  // Søk + filter ligger i egen verktøyrad over lista, ikke i sidehodet — sidehodet
  // blir da liggende på én linje også på smale skjermer.
  const listTools = <div className="fak-list-tools">
    <SearchField value={search} onChange={setSearch} placeholder={t(rowList.active ? 'inv.rows.searchPh' : 'inv.searchPh')} help={t(rowList.active ? 'inv.rows.searchHelp' : 'inv.searchHelp')} />
    {filterEl}
  </div>;
  return <div>
    <ListShell title={t('inv.title')} searchPh={t('inv.searchPh')} noHeaderSearch
    help titleHelp={t('inv.titleHelp')} searchHelp={t(rowList.active ? 'inv.rows.searchHelp' : 'inv.searchHelp')} downloadHelp={t('inv.downloadHelp')} addHelp={t('inv.addHelp')}
    search={search} setSearch={setSearch} onAdd={() => setRoute && setRoute('faktura')} addLabel={t('inv.add')}
    exportData={exportData}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
        <InvoiceListBackToBilagButton show={showBilagReturn} setRoute={setRoute} />
        <FakturaTabs tab={tab} draftCount={rowList.active ? (stats.drafts ?? 0) : draftItems.length} onSelect={(k) => { setTab(k); setFilter(makeFilter()); setExp(null); clearSelection(); }}
          helpTitle={t('inv.tabsHelpTitle')} help={t('inv.tabsHelp')} />
      </div>
      {importedSalesHistoryNeedsReanalysis && <div className="alert alert-warning mt-16" style={{ alignItems: 'center' }}>
        <span className="alert-ico"><Icon name="alertTri" size={18} /></span><div className="grow"><b>{t('inv.importHistory.reanalysisTitle')}</b><div className="t-sm mt-4" style={{ color: 'var(--ink-2)' }}>{t('inv.importHistory.reanalysisBody')}</div></div>
        <Button variant="secondary" size="sm" icon="refresh" onClick={openAccountingImportReanalysis}>{t('inv.importHistory.reanalysisAction')}</Button>
      </div>}
      {/* Et dokument som kom i retur er en oppgave, ikke en detalj. Avvisningen kan
          komme timer etter sendingen — bruker har for lengst forlatt fakturaen og tror
          den er framme. Derfor staar den ovest i lista, ikke bare i raden. */}
      {failedDeliveries.length > 0 && <div className="alert alert-error mt-16" style={{ alignItems: 'center' }}>
        <span className="alert-ico"><Icon name="alertTri" size={18} /></span>
        <div className="grow"><b>{t('inv.delivery.bannerTitle', { n: failedDeliveries.length })}</b>
          <div className="t-sm mt-4" style={{ color: 'var(--ink-2)' }}>{t('inv.delivery.bannerBody', { documents: failedDeliveries.slice(0, 4).map((entry) => entry.num || '-').join(', ') })}</div></div>
      </div>}
      {tab === 'importhistorikk' ? <window.ImportedSalesHistory year={Number(year || model.year)} /> : tab === 'salgsrapport' ? <SalesReport rows={ofYear} filter={filter} setRoute={setRoute} filterEl={filterEl} /> : <React.Fragment>
      <div className="grid-4 mt-20" style={{ marginBottom: 20 }}>
        {tabKpis.map(([tint, icon, kpiId, value, sub], i) => <KPI key={i} tint={tint} icon={icon} label={fakKpiLabel(kpiId)} value={value} sub={sub} help={fakKpiHelp(kpiId)} />)}
      </div>
      {listTools}
      {selectableRows.length > 0 && <InvoiceBulkBar tab={tab} rows={list} selectedRows={selectedRows} allSelected={allSelected} someSelected={someSelected} onSelectAll={toggleSelectVisible} onClear={clearSelection} onAction={setBulkAction} />}
      {backend?.loading || (rowList.active && rowList.loading && list.length === 0) ? <div className="col center" style={{ textAlign: 'center', padding: '30px 20px', gap: 6 }}><h3 className="t-h2">{t('inv.loading')}</h3><p className="muted" style={{ maxWidth: 380, fontWeight: 600 }}>{t('inv.loadingSub')}</p></div> :
      list.length === 0 ?
      <div className="col center" style={{ textAlign: 'center', padding: '30px 20px', gap: 6 }}><h3 className="t-h2">{t('inv.noMatch')}</h3><p className="muted" style={{ maxWidth: 380, fontWeight: 600 }}>{t('inv.noMatchSub')}</p></div> :
      <div data-help-title={t('inv.listHelpTitle')} data-help={t('inv.listHelp')}>
      <DataList tableClass="fak-tbl" rows={list} rowKey={(inv) => inv.id}
        rowClass={(inv) => highlight && (String(inv.num || '') === String(highlight) || String(inv.id || '') === String(highlight)) ? 'row-flash' : ''}
        onRowClick={(inv, event) => {
          if ((event?.shiftKey || event?.ctrlKey || event?.metaKey) && invoiceListCanBulkSelect(inv)) {
            setSelectionRange(inv, event);
            return;
          }
          setExp(exp === inv.id ? null : inv.id);
        }}
        expandedKey={exp} renderExpanded={(inv) => inv.importedReadOnly ? <ImportedInvoiceDetail inv={inv} onStatusChange={() => setImportedStatusRefresh((value) => value + 1)} /> : rowsMode ? <InvoiceRowDetail invoice={inv}><FakturaDetail inv={inv} setRoute={setRoute} paymentAccounts={model.paymentAccounts} /></InvoiceRowDetail> : <FakturaDetail inv={inv} setRoute={setRoute} paymentAccounts={model.paymentAccounts} />}
        columns={[
          { header: <button type="button" className={'inbox-check' + (allSelected ? ' on' : '') + (someSelected && !allSelected ? ' partial' : '')} onClick={(event) => { event.stopPropagation(); toggleSelectVisible(); }} title={t('inv.bulk.selectAll')} aria-label={t('inv.bulk.selectAll')} data-help={t('helpTopics.bulk.body')} data-help-title={t('helpTopics.bulk.title')}>{allSelected && <Icon name="check" size={13} stroke={3} />}</button>, sortable: false, thClass: 'fak-select-col', cellClass: 'fak-select-col', render: (inv) => invoiceListCanBulkSelect(inv) ? <button type="button" className={'inbox-check' + (selectedIds.has(inv.id) ? ' on' : '')} onClick={(event) => { event.stopPropagation(); toggleSelection(inv, event); }} title={t('inv.bulk.selectRow')} aria-label={t('inv.bulk.selectDocument')}>{selectedIds.has(inv.id) && <Icon name="check" size={13} stroke={3} />}</button> : null },
          { header: t(tab === 'utkast' ? 'inv.col.document' : 'inv.col.invoice'), render: (inv) => <div className="row gap-12">
            <span className="icon-chip fak-ico" style={{ background: inv.amount < 0 ? 'var(--error-bg)' : 'var(--plum-100)', color: inv.amount < 0 ? 'var(--error)' : 'var(--plum-700)' }}><Icon name={inv.type === 'kreditnota' ? 'files' : 'fileText'} size={18} /></span>
             <div><div className="t-h3" style={{ fontSize: 14.5 }}>{inv.num || t('inv.draftPlaceholder')}</div><div className="t-sm muted">{invoiceListRowSubtitle(inv, tab)}</div>{inv.importedReadOnly && <div className="t-xs muted" style={{ fontWeight: 700 }}>{t('inv.importHistory.rowSource')}</div>}<div className="t-sm muted fak-date-inline">{inv.date}</div></div>
          </div> },
          { header: t('inv.col.date'), thClass: 'fak-date-col', cellClass: 't-sm muted fak-date-col', render: (inv) => inv.date },
          { header: t('inv.col.net'), headerRight: true, thClass: 'fak-net', cellClass: 'num fak-net', render: (inv) => fmt(inv.net) },
          { header: t('inv.col.amount'), headerRight: true, cellClass: 'num', tdStyle: (inv) => ({ color: inv.amount < 0 ? 'var(--error)' : 'var(--ink)' }), render: (inv) => <React.Fragment>{fmt(inv.amount)}<div className="fak-net-sub">{t('inv.netInline', { amount: fmt(inv.net) })}</div></React.Fragment> },
          // «Sendt» betyr bare at e-posttjenesten tok imot den. Kom den i retur, sto
          // det til na bare inne i den utvidede detaljen — og en avvisning som ingen
          // apner er like usynlig som ingen avvisning. Radene sier det selv na.
          { header: t('inv.col.status'), thClass: 'fak-status', cellClass: 'fak-status', sort: (inv) => STATUS_SORT_ORDER[inv.status] || 99, render: (inv) => <div className="col gap-4" style={{ alignItems: 'flex-start' }}>
            <StatusBadge status={inv.status} />
            {inv.delivery?.status === 'external' && <span className="t-xs muted" style={{ fontWeight: 700 }}>{t('inv.delivery.external')}</span>}
            {inv.delivery?.failed && <span className="row gap-4 t-xs" style={{ alignItems: 'center', fontWeight: 800, color: 'var(--error)' }}><Icon name="alertTri" size={12} />{t('inv.delivery.rowFailed')}</span>}
          </div> },
        ]} /></div>}
      <InvoiceRowListFooter rowList={rowList} navigationDisabled={Boolean(bulkAction || bulkBusy)} onNavigate={() => { clearSelection(); setExp(null); }} />
      {bulkAction && <InvoiceBulkDialog action={bulkAction} rows={selectedRows} busy={Boolean(bulkBusy)} initial={window.__plummeInvoiceListBulkIntent?.operation===bulkAction?window.__plummeInvoiceListBulkIntent.options:null} onClose={() => {window.__plummeInvoiceListBulkIntent=null;setBulkAction(null);}} onConfirm={runBulkAction} />}
      </React.Fragment>}
    </ListShell>
  </div>;
}

Object.assign(window, { FakturaDetail, FakturalistePage, invoiceListCreditableLines });
